import { useState, useEffect, useMemo } from 'react';
import DateAvisNia, { DatesAvisNia } from './DateAvisNia';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CandlestickData, LineData } from 'lightweight-charts';
import ReactMarkdown from 'react-markdown';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { useGamificationContext } from '../../context/GamificationContext';
import SiteLayout from '../../layouts/SiteLayout';
import './DataScreen.css';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import Loader from '../../components/Common/Loader';
import StockSearch from '../../components/Common/StockSearch';
import AdvancedChart, {
  MarqueurEvenement,
} from '../../components/Charts/AdvancedChart';
import IndicatorChart from '../../components/Charts/IndicatorChart';
import { useLynchClassification } from '../../hooks/useLynchClassification';
import StockNotesPanel from '../../components/StockNotes/StockNotesPanel';
import StockOverview from '../StockDetails/tabs/StockOverview';
import MobileTerminal from './MobileTerminal';
import { useScreenType } from '../../hooks/useScreenType';
import usePageTitle from '../../hooks/usePageTitle';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Label,
  ComposedChart,
  Area,
} from 'recharts';

import {
  calculateSMA,
  calculateRSI,
  calculateMACD,
  calculateBollingerBands,
  interpretMarket,
} from '../../utils/indicators';

type ChartType = 'line' | 'candlestick' | 'heikin-ashi' | 'area';
type ChartPeriod = '1W' | '1M' | '3M' | '6M' | '1Y' | '5Y' | '10Y';

// ⚠️ `jours` et `limit` ne sont PAS la même unité, et les confondre était le bug.
//   `jours` = largeur de la fenêtre affichée, en jours CALENDAIRES.
//   `limit` = nombre de LIGNES de cotation demandées à l'API, volontairement large.
// Ces valeurs étaient à l'origine des jours, mais servaient de compte de lignes : « 5 Ans »
// affichait donc les 1 825 dernières SÉANCES, soit ~7,3 années de bourse (≈250 séances/an).
// C'est ce qui faisait démarrer le graphe de SOLIBRA en 2017. Le découpage se fait
// désormais sur la date ; `limit` ne sert plus qu'à charger de quoi couvrir la fenêtre.
const CHART_PERIOD_OPTIONS: {
  value: ChartPeriod;
  label: string;
  jours: number;
  limit: number;
}[] = [
  { value: '1W', label: '1 semaine', jours: 7, limit: 10 },
  { value: '1M', label: '1 mois', jours: 30, limit: 30 },
  { value: '3M', label: '3 mois', jours: 90, limit: 80 },
  { value: '6M', label: '6 mois', jours: 180, limit: 150 },
  { value: '1Y', label: '1 an', jours: 365, limit: 300 },
  { value: '5Y', label: '5 Ans', jours: 1825, limit: 1400 },
  { value: '10Y', label: '10 ans', jours: 3650, limit: 2800 },
];

// Borne basse de la fenêtre affichée. Une largeur en jours calendaires est la seule
// unité stable : le nombre de séances par an varie (jours fériés, et surtout trous
// d'historique — 2016 n'a que 63 séances en base, 2021 en a 156 sur ~250).
const borneDePeriode = (jours: number): string => {
  const borne = new Date();
  borne.setDate(borne.getDate() - jours);
  return borne.toISOString().slice(0, 10);
};

interface Stock {
  symbole: string;
  nom_societe: string;
  secteur?: string;
  compartiment?: string;
}

interface StockDetails {
  symbole: string;
  nom_societe: string;
  secteur: string;
  compartiment?: string;
}

interface Quotation {
  date_cotation?: string;
  date_seance?: string;
  cours_cloture: number;
  variation_jour_pct: number;
  variation_annee_pct?: number;
  volume: number;
  valeur_echange?: number;
  cours_ouverture?: number;
  cours_precedent?: number;
  volume_echange?: number;
  cours_bas?: number;
  cours_haut?: number;
}

interface Capitalisation {
  symbole: string;
  cours_cloture: number | null;
  quantite_totale_titres: number | null;
  quantite_titres_flottant: number | null;
  flottant_pct: number | null;
  diffusion_15pct_respectee: boolean;
  capitalisation_boursiere: number | null;
  capitalisation_flottante: number | null;
}

interface Dividende {
  societe_symbole: string;
  date_paiement: string | null;
  montant_net: number;
  montant_brut?: number | null;
  exercice?: number | null;
  statut?: string;
}

// Année à utiliser pour trier/regrouper une ligne dividende — statut='AUCUN' (décision explicite
// de non-distribution) n'a pas de date_paiement (backend nullable, cf. schemas.py), donc on
// retombe sur `exercice` plutôt que de dériver l'année d'une date null.
const getDividendeYear = (d: Dividende): number => {
  if (d.exercice) return d.exercice;
  if (d.date_paiement) return new Date(d.date_paiement).getFullYear();
  return 0;
};

// Montant à afficher et à analyser : le BRUT directement (ou repli sur net si brut absent).
const getDividendeMontantBrut = (d: Dividende): number | null =>
  d.montant_brut != null ? d.montant_brut : (d.montant_net != null ? d.montant_net : null);

// Clé de tri : la DATE DE PAIEMENT, pas l'exercice. C'est l'événement que l'utilisateur
// cherche (« quand ai-je touché quoi »), et les deux ordres divergent : un exercice peut
// être payé en plusieurs fois, et un rattrapage peut intervenir après un exercice plus
// récent. Trier sur l'exercice laissait aussi les lignes d'un même exercice dans un ordre
// arbitraire.
// statut='AUCUN' n'a pas de date_paiement (décision de non-distribution) : on l'ancre au
// milieu de l'année suivant l'exercice, période habituelle de paiement à la BRVM, pour
// qu'il apparaisse à sa place chronologique plutôt que rejeté en fin de liste. C'est un
// ordre d'affichage, aucune date n'est inventée ni stockée.
const getDividendeDateTri = (d: Dividende): number =>
  d.date_paiement
    ? new Date(d.date_paiement).getTime()
    : new Date(getDividendeYear(d) + 1, 5, 30).getTime();

// Antéchronologique : les plus récents en premier.
const parPaiementDecroissant = (a: Dividende, b: Dividende): number =>
  getDividendeDateTri(b) - getDividendeDateTri(a);

const DataScreen: React.FC = () => {
  const { token, hasPermission, user } = useAuth();
  // Fonctionnalités en cours de fiabilisation (classification Lynch, graphe historique
  // du prix juste) — réservées admin le temps de les observer en conditions réelles
  // avant exposition aux clients Pro.
  const isAdmin = hasPermission('admin_platform');
  const { trackAction } = useGamificationContext();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Tab mapping for readable titles
  const tabNames: Record<string, string> = {
    overview: 'Résumé',
    orderbook: "Carnet d'ordres",
    analysis: 'Analyse Technique',
    dividends: 'Dividendes',
    valuation: 'Valorisation (Vrai Prix)',
    ai_summary: 'Analyse IA Nia',
    peers: 'Comparaison (Pairs)',
  };

  // Initialize tab from URL — advanced tabs are validated against permissions at render time
  const initialTab = searchParams.get('tab') as any;
  const [activeTab, setActiveTab] = useState<
    'overview' | 'orderbook' | 'analysis' | 'dividends' | 'valuation' | 'ai_summary' | 'peers'
  >(initialTab && tabNames[initialTab] ? initialTab : 'overview');

  const [selectedStock, setSelectedStock] = useState<string>(searchParams.get('symbol') || '');

  // Classification Lynch — réservée admin (cf. isAdmin), le hook ne fait rien si
  // l'appel échoue en 403 pour un non-admin.
  const { data: lynchClassification } = useLynchClassification(isAdmin ? selectedStock : null);

  const ALLOWED_DEMO_SYMBOLS = ['SNTS', 'ORAC', 'CIEC'];
  const hasPermissionForStock = (permission: string): boolean => {
    if (user?.role?.name === 'demo_user' && selectedStock) {
      const advanced_permissions = [
        'view_advanced_data',
        'view_advanced_ai_data',
        'trader_prudent',
        'view_watchlist_ia',
      ];
      if (advanced_permissions.includes(permission)) {
        if (!ALLOWED_DEMO_SYMBOLS.includes(selectedStock.toUpperCase())) {
          return false;
        }
      }
    }
    return hasPermission(permission);
  };

  const [chartType, setChartType] = useState<ChartType>('area'); // Default to area
  const [chartPeriod, setChartPeriod] = useState<ChartPeriod>('1Y'); // Default to 1Y for better view
  const [stocks, setStocks] = useState<Stock[]>([]);

  // Update document title dynamically
  const displayTabName = tabNames[activeTab] || 'Terminal';
  usePageTitle(selectedStock ? `${displayTabName} - ${selectedStock}` : displayTabName);

  // Stock Specific Data (Overview)
  const [stockDetails, setStockDetails] = useState<StockDetails | null>(null);
  const [capitalisation, setCapitalisation] = useState<Capitalisation | null>(null);
  const [quotations, setQuotations] = useState<Quotation[]>([]); // Raw history for Overview
  const [isNotesPanelOpen, setIsNotesPanelOpen] = useState<boolean>(false);
  const { isMobile } = useScreenType();

  // State → URL (one-way). Reads the query fresh from window.location to avoid
  // depending on `searchParams`, whose stale closure caused a selection ping-pong
  // when switching stocks (state and URL fighting each other).
  useEffect(() => {
    if (!selectedStock && !activeTab) return;
    const currentSearch = new URLSearchParams(window.location.search).toString();
    const newParams = new URLSearchParams(window.location.search);
    if (selectedStock) newParams.set('symbol', selectedStock);
    if (activeTab) newParams.set('tab', activeTab);
    const newSearch = newParams.toString();
    if (newSearch !== currentSearch) {
      navigate(`/data?${newSearch}`, { replace: true });
    }
  }, [selectedStock, activeTab, navigate]);

  // URL → state, only for external URL changes (link from another page, back/forward).
  // No-op when the URL already matches the state, which prevents any sync loop.
  useEffect(() => {
    const urlSymbol = searchParams.get('symbol');
    if (urlSymbol && urlSymbol !== selectedStock) setSelectedStock(urlSymbol);
    const urlTab = searchParams.get('tab');
    if (urlTab && tabNames[urlTab] && urlTab !== activeTab) setActiveTab(urlTab as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Data State
  const [chartData, setChartData] = useState<CandlestickData[]>([]);
  const [volumeData, setVolumeData] = useState<any[]>([]); // HistogramData
  // Repères d'opérations sur titres. Purement additifs : à vide, le graphique est
  // rigoureusement identique. Sert à vérifier la série de cours — le marqueur dit où
  // une discontinuité doit apparaître, donc si l'ajustement fait bien son travail.
  const [marqueursEvenements, setMarqueursEvenements] = useState<MarqueurEvenement[]>([]);
  const [afficherMarqueurs, setAfficherMarqueurs] = useState<boolean>(false);

  // Visualization State
  const [overlays, setOverlays] = useState({
    sma20: true,
    sma50: false,
    sma200: false,
    bollinger: false,
    volume: true,
  });

  // Analysis State
  const [marketAnalysis, setMarketAnalysis] = useState<string>('');
  const [dividendData, setDividendData] = useState<Dividende[]>([]);
  const [isFavorited, setIsFavorited] = useState<boolean>(false);

  // Indicator Data State
  const [indicatorData, setIndicatorData] = useState<{
    price: CandlestickData[];
    sma20: LineData[];
    sma50: LineData[]; // Added
    sma200: LineData[]; // Added
    rsi: LineData[];
    macd: {
      macdLine: LineData[];
      signalLine: LineData[];
      histogram: { time: string; value: number; color: string }[];
    };
    bollinger: {
      upper: LineData[];
      lower: LineData[];
      sma: LineData[];
    };
  } | null>(null);

  const [loading, setLoading] = useState<boolean>(false);

  // Valuation State
  const [valuationData, setValuationData] = useState<any>(null);
  const [valuationLoading, setValuationLoading] = useState<boolean>(false);
  const [marketMultiplesData, setMarketMultiplesData] = useState<any>(null);
  const [mobileValuationExpanded, setMobileValuationExpanded] = useState<boolean>(false);

  // Valuation History State
  interface ValuationHistoryPoint {
    date: string;
    cours_actuel: number;
    prix_pondere: number;
    potentiel_pct: number;
    recommendation: string;
    fiabilite: string;
    nb_modeles_valides: number;
  }
  const [valuationHistory, setValuationHistory] = useState<ValuationHistoryPoint[]>([]);
  const [valuationHistoryLoading, setValuationHistoryLoading] = useState<boolean>(false);
  const [valuationHistoryPeriod, setValuationHistoryPeriod] = useState<'1M' | '3M' | '6M' | 'MAX'>(
    'MAX'
  );

  // AI Narrative State
  const [aiNarrative, setAiNarrative] = useState<string>('');
  const [aiNarrativeDates, setAiNarrativeDates] = useState<DatesAvisNia | null>(null);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [aiSummaryType, setAiSummaryType] = useState<'VALUATION' | 'TECHNICAL'>('VALUATION');

  // Peer Comparison States
  const [peersData, setPeersData] = useState<any[]>([]);
  const [peersLoading, setPeersLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchStocks = async () => {
      try {
        const response = await fetch(`${API_URL}/v1/stocks/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const rawData = await response.json();

        // Guard: filter out any entries with null/empty symbole or nom_societe
        // (can happen with incomplete DB entries in production)
        const data: Stock[] = Array.isArray(rawData)
          ? rawData.filter((s: Stock) => s && s.symbole)
          : [];

        // Sort stocks alphabetically by symbol
        data.sort((a: Stock, b: Stock) => a.symbole.localeCompare(b.symbole));
        setStocks(data);
      } catch (error) {
        console.error('Error fetching stocks:', error);
      }
    };

    if (token) fetchStocks();
  }, [token]);

  useEffect(() => {
    if (selectedStock) {
      // Reset states before fetching new stock data to avoid stale display
      setStockDetails(null);
      setQuotations([]);
      setCapitalisation(null);
      fetchStockDetails();
      fetchCapitalisation();
      fetchChartData();
      fetchDividendData();
      checkFavoriteStatus();
      setAiNarrative(''); // Reset AI narrative when stock changes
      if (activeTab === 'valuation') fetchValuationData();
      if (activeTab === 'ai_summary') fetchAINarrative(); // Auto-fetch if tab is active
      if (activeTab === 'peers') fetchPeersData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStock, chartType, chartPeriod]);

  // Separate effect for tab switch to AI / Valuation / Dividends
  useEffect(() => {
    if (selectedStock) {
      if (activeTab === 'ai_summary') {
        if (!aiNarrative) fetchAINarrative();
        trackAction('VIEW_AI_ANALYSIS');
      }
      if (activeTab === 'valuation') {
        fetchValuationData();
        // L'API réserve cet historique à admin_platform : sans la permission, l'appel finissait en 403.
        if (isAdmin) fetchValuationHistory();
        trackAction('VIEW_FAIR_VALUE');
      }
      if (activeTab === 'peers') {
        fetchPeersData();
        trackAction('VIEW_PEERS_COMPARISON');
      }
      if (activeTab === 'dividends') {
        trackAction('VIEW_DIVIDENDS');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedStock, trackAction]);

  const fetchStockDetails = async () => {
    if (!selectedStock) return;
    try {
      const response = await fetch(`${API_URL}/v1/stocks/${selectedStock}/details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const details = await response.json();
        setStockDetails(details);
      }
    } catch (error) {
      console.error('Error fetching stock details:', error);
    }
  };

  const fetchCapitalisation = async () => {
    if (!selectedStock) return;
    try {
      const response = await fetch(`${API_URL}/v1/stocks/${selectedStock}/capitalisation`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCapitalisation(data);
      }
    } catch (error) {
      console.error('Error fetching capitalisation:', error);
    }
  };

  const fetchDividendData = async () => {
    try {
      const response = await fetch(`${API_URL}/v1/stocks/${selectedStock}/dividends`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setDividendData(data);
      } else {
        setDividendData([]);
      }
    } catch (error) {
      console.error('Error fetching dividends:', error);
      setDividendData([]);
    }
  };

  const fetchValuationData = async () => {
    if (!selectedStock) return;
    setValuationLoading(true);
    try {
      // Fetch complete valuation with all 9 models and synthesis
      const response = await fetch(`${API_URL}/v1/valuation/${selectedStock}/complete`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setValuationData(data);
        setMarketMultiplesData(data.market_multiples);
      } else {
        // Fallback to basic valuation if complete fails
        const basicResponse = await fetch(`${API_URL}/v1/valuation/${selectedStock}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (basicResponse.ok) {
          setValuationData(await basicResponse.json());
        } else {
          setValuationData(null);
        }
        setMarketMultiplesData(null);
      }
    } catch (error) {
      console.error('Error fetching valuation:', error);
      setValuationData(null);
      setMarketMultiplesData(null);
    }
    setValuationLoading(false);
  };

  const fetchValuationHistory = async () => {
    if (!selectedStock) return;
    setValuationHistoryLoading(true);
    try {
      const response = await fetch(`${API_URL}/v1/valuation/${selectedStock}/history?limit=180`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setValuationHistory(data.data || []);
      } else {
        setValuationHistory([]);
      }
    } catch (error) {
      console.error('Error fetching valuation history:', error);
      setValuationHistory([]);
    }
    setValuationHistoryLoading(false);
  };

  const fetchPeersData = async () => {
    if (!selectedStock) return;
    setPeersLoading(true);
    try {
      const response = await fetch(`${API_URL}/v1/stocks/${selectedStock}/peers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setPeersData(data);
      } else {
        setPeersData([]);
      }
    } catch (error) {
      console.error('Error fetching peers data:', error);
      setPeersData([]);
    }
    setPeersLoading(false);
  };

  const checkFavoriteStatus = async () => {
    if (!selectedStock) return;
    try {
      const response = await fetch(`${API_URL}/api/favorites/${selectedStock}/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setIsFavorited(data.is_favorited);
      }
    } catch (error) {
      console.error('Error checking favorite status:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!token || !selectedStock) return;
    try {
      const method = isFavorited ? 'DELETE' : 'POST';
      const response = await fetch(`${API_URL}/api/favorites/${selectedStock}`, {
        method,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setIsFavorited(!isFavorited);
        if (!isFavorited) {
          trackAction('ADD_FAVORITE');
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // ... (existing fetchDividendData, fetchValuationData)

  const fetchAINarrative = async (type: 'VALUATION' | 'TECHNICAL' = aiSummaryType) => {
    if (!selectedStock) return;
    setAiLoading(true);
    // setAiNarrative(''); // Optional: Clear or keep old while loading? Better to keep old or show loader overlay.
    // Let's clear to avoid confusion
    if (type !== aiSummaryType) setAiNarrative('');
    // Date du texte : demandée à part, un échec ne doit pas empêcher d'afficher l'avis.
    setAiNarrativeDates(null);
    const valeurDemandee = selectedStock;
    fetch(`${API_URL}/v1/ai/narrative/${selectedStock}/dates?type=${type}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((corps) => {
        if (corps?.data && corps.data.symbole === valeurDemandee) setAiNarrativeDates(corps.data);
      })
      .catch(() => setAiNarrativeDates(null));

    try {
      const response = await fetch(`${API_URL}/v1/ai/narrative/${selectedStock}?type=${type}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const text = await response.json();
        setAiNarrative(text);
      } else {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail || "Impossible de récupérer l'analyse.";

        if (response.status === 404) {
          setAiNarrative(
            `⏳ Analyse non disponible pour le moment.\n\nLe traitement automatique n'a pas encore été finalisé pour aujourd'hui. Veuillez revenir plus tard.`
          );
        } else if (response.status === 503) {
          setAiNarrative(`❌ Erreur de génération : ${detail}`);
        } else {
          setAiNarrative(`⚠️ ${detail}`);
        }
      }
    } catch (error) {
      console.error('Error fetching AI narrative:', error);
      setAiNarrative(
        '❌ Erreur de connexion au service. Veuillez vérifier votre connexion internet.'
      );
    }
    setAiLoading(false);
  };

  // Effect to refetch when switching types (only if stock selected and tab active)
  useEffect(() => {
    if (activeTab === 'ai_summary' && selectedStock) {
      fetchAINarrative(aiSummaryType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiSummaryType, activeTab, selectedStock]);

  // Chargement des repères d'opérations sur titres. Isolé de `fetchChartData` : un
  // échec ici ne doit JAMAIS empêcher l'affichage du cours — ce sont des repères de
  // contrôle, pas la donnée principale.
  const fetchMarqueursEvenements = async () => {
    if (!selectedStock || !token) return;
    try {
      const r = await fetch(
        `${API_URL}/v1/stocks/${selectedStock}/corporate-actions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = await r.json();
      setMarqueursEvenements(Array.isArray(d) ? d : []);
    } catch (e) {
      console.warn('fetchMarqueursEvenements:', e);
      setMarqueursEvenements([]);
    }
  };

  useEffect(() => {
    if (afficherMarqueurs) fetchMarqueursEvenements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStock, afficherMarqueurs, token]);

  // ⚠️ NE MONTRER QUE LES REPERES DE LA FENETRE AFFICHEE, et dire combien sont hors
  // champ. Le compteur affichait d'abord le TOTAL : sur SIBC il annonçait « (3) » alors
  // que les trois operations — 2018, nov. 2024 — tombent toutes avant la fenetre par
  // defaut d'un an, donc rien ne s'affichait. Un compteur qui promet ce que le graphique
  // ne montre pas est pire que pas de compteur.
  const marqueursVisibles = useMemo(() => {
    if (!chartData.length || !marqueursEvenements.length) return [];
    const debut = String(chartData[0].time);
    const fin = String(chartData[chartData.length - 1].time);
    return marqueursEvenements.filter(
      (m) => m.date_effective >= debut && m.date_effective <= fin
    );
  }, [chartData, marqueursEvenements]);

  const fetchChartData = async () => {
    setLoading(true);
    try {
      // Get limit based on selected period (use max for indicators)
      const periodConfig = CHART_PERIOD_OPTIONS.find((p) => p.value === chartPeriod);
      const chartLimit = periodConfig?.limit || 22;
      // Borne d'affichage en jours calendaires — distincte du nombre de lignes chargées.
      const borneAffichage = borneDePeriode(periodConfig?.jours ?? 30);
      // For indicators, always fetch 200 points minimum
      const indicatorLimit = Math.max(200, chartLimit);

      const response = await fetch(
        `${API_URL}/v1/stocks/${selectedStock}/history?limit=${indicatorLimit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();

      // Guard: API may return an error object (e.g. 403) instead of an array
      if (!Array.isArray(data)) {
        console.warn('fetchChartData: unexpected response (not an array):', data);
        setQuotations([]);
        setLoading(false);
        return;
      }

      // Store Raw Data for Overview
      setQuotations(data);

      const sortedData = [...data].reverse(); // Oldest first
      const validData = sortedData.filter(
        (item: any) => item.cours_cloture !== null && item.cours_cloture !== undefined
      );

      // Run Analysis
      const analysisText = interpretMarket(validData);
      setMarketAnalysis(analysisText);

      // --- Fenêtre affichée : bornée par la DATE, jamais par un nombre de séances ---
      // Compter les lignes revenait à afficher « les N dernières séances », dont la durée
      // dépend de la densité de l'historique : 1 825 séances valent ~7,3 ans, et bien plus
      // encore sur les périodes trouées. On retombe sur les N dernières lignes disponibles
      // seulement si la fenêtre est vide, pour ne jamais rendre un graphe blanc.
      const parDate = validData.filter((item: any) => item.date_cotation >= borneAffichage);
      const chartDataSlice = parDate.length > 0 ? parDate : validData.slice(-chartLimit);

      // Map to generic CandlestickData (OHLC)
      // AdvancedChart will handle Heikin-Ashi or Line transformation if needed
      const ohlcData: CandlestickData[] = chartDataSlice.map((item: any) => ({
        time: item.date_cotation,
        open:
          Number(item.cours_ouverture) ||
          Number(item.cours_precedent) ||
          Number(item.cours_cloture),
        high: Math.max(Number(item.cours_ouverture) || 0, Number(item.cours_cloture)),
        low: Math.min(Number(item.cours_ouverture) || 0, Number(item.cours_cloture)),
        close: Number(item.cours_cloture),
      }));
      setChartData(ohlcData);

      // Map Volume Data
      const volData = chartDataSlice.map((item: any) => {
        const close = Number(item.cours_cloture);
        const open = Number(item.cours_ouverture) || Number(item.cours_precedent) || close;
        return {
          time: item.date_cotation,
          value: Number(item.volume_echange) || 0,
          color: close >= open ? '#26a69a' : '#ef5350', // Green if up, Red if down
        };
      });
      setVolumeData(volData);

      // --- Calculate Indicators ---
      const closePrices = validData.map((d: any) => Number(d.cours_cloture));
      const dates = validData.map((d: any) => d.date_cotation);

      // SMA 20
      const sma20Values = calculateSMA(closePrices, 20);
      const sma20Data = sma20Values
        .map((val, i) => ({ time: dates[i + 19], value: val.value }))
        .filter((d) => d.time);

      // SMA 50
      const sma50Values = calculateSMA(closePrices, 50);
      const sma50Data = sma50Values
        .map((val, i) => ({ time: dates[i + 49], value: val.value }))
        .filter((d) => d.time);

      // SMA 200
      const sma200Values = calculateSMA(closePrices, 200);
      const sma200Data = sma200Values
        .map((val, i) => ({ time: dates[i + 199], value: val.value }))
        .filter((d) => d.time);

      // ⚠️ FILET DE SECURITE : aucune valeur non finie ne doit atteindre setData.
      // lightweight-charts rejette la serie ENTIERE des qu'un seul point est NaN ou
      // Infini — un indicateur defaillant fait alors disparaitre tout le graphique.
      // La cause connue est corrigee en amont (RSI sur serie plate, cf. indicators.ts),
      // mais un indicateur reste un calcul : on ne laisse pas un cas limite non prevu
      // emporter l'affichage du cours, qui est la donnee principale.
      const fini = <T extends { time: any; value: number }>(pts: T[]): T[] =>
        pts.filter((d) => d.time && Number.isFinite(d.value));

      // RSI 14
      const rsiValues = calculateRSI(closePrices, 14);
      // RSI array length is data.length - 14. We need to align dates.
      // RSI[0] corresponds to data[14] (15th element)
      const rsiData = rsiValues.map((val, i) => ({ time: dates[i + 14], value: val }));

      // MACD (12, 26, 9)
      const { macdLine, signalLine, histogram } = calculateMACD(closePrices);
      const macdData = macdLine.map((val, i) => ({ time: dates[i], value: val }));
      const signalData = signalLine.map((val, i) => ({ time: dates[i], value: val }));
      const histogramData = histogram.map((val, i) => ({
        time: dates[i],
        value: val,
        color: val >= 0 ? '#26a69a' : '#ef5350',
      }));

      // Bollinger Bands (20, 2)
      const { upper, lower, sma } = calculateBollingerBands(closePrices, 20, 2);
      const bbUpper = upper.map((val, i) => ({ time: dates[i + 19], value: val }));
      const bbLower = lower.map((val, i) => ({ time: dates[i + 19], value: val }));
      const bbSma = sma.map((val, i) => ({ time: dates[i + 19], value: val }));

      // Price Data for Indicators Chart (Candlestick)
      const priceDataForInd = validData.map((item: any) => ({
        time: item.date_cotation,
        open:
          Number(item.cours_ouverture) ||
          Number(item.cours_precedent) ||
          Number(item.cours_cloture),
        high: Math.max(Number(item.cours_ouverture) || 0, Number(item.cours_cloture)),
        low: Math.min(Number(item.cours_ouverture) || 0, Number(item.cours_cloture)),
        close: Number(item.cours_cloture),
      }));

      setIndicatorData({
        price: priceDataForInd,
        sma20: fini(sma20Data),
        sma50: fini(sma50Data),
        sma200: fini(sma200Data),
        rsi: fini(rsiData),
        macd: {
          macdLine: fini(macdData),
          signalLine: fini(signalData),
          histogram: fini(histogramData),
        },
        bollinger: {
          upper: fini(bbUpper),
          lower: fini(bbLower),
          sma: fini(bbSma),
        },
      });
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
    setLoading(false);
  };

  // Helpers for StockOverview
  const calculatePerformance = (days: number): { value: string; isPositive: boolean } => {
    if (!Array.isArray(quotations) || quotations.length < days)
      return { value: 'N/A', isPositive: false };
    const latestPrice = quotations[0]?.cours_cloture || 0;
    const oldPrice = quotations[days - 1]?.cours_cloture || 0;
    if (oldPrice === 0) return { value: 'N/A', isPositive: false };
    const performance = ((latestPrice - oldPrice) / oldPrice) * 100;
    return {
      value: `${performance > 0 ? '+' : ''}${performance.toFixed(2)}%`,
      isPositive: performance >= 0,
    };
  };

  const getYTDPerformance = (): { value: string; isPositive: boolean } => {
    if (!Array.isArray(quotations)) return { value: '0.00%', isPositive: true };
    const latestQuotation = quotations[0];
    // null pour une valeur sans historique annuel (nouvelle admission) : == null couvre null et undefined
    if (!latestQuotation || latestQuotation.variation_annee_pct == null) {
      return { value: '0.00%', isPositive: true };
    }
    const perf = latestQuotation.variation_annee_pct;
    return {
      value: `${perf > 0 ? '+' : ''}${perf.toFixed(2)}%`,
      isPositive: perf >= 0,
    };
  };

  const getWeeklyBreakdown = () => {
    if (!Array.isArray(quotations) || quotations.length < 5) return [];
    return quotations.slice(0, 5).map((q: any) => ({
      day: new Date(q.date_cotation || q.date_seance).toLocaleDateString('fr-FR', {
        weekday: 'short',
      }),
      date: new Date(q.date_cotation || q.date_seance).toLocaleDateString('fr-FR'),
      price: q.cours_cloture,
      change: q.variation_jour_pct,
      volume: q.volume_echange || q.volume,
    }));
  };

  const formatLargeNumber = (num: number | null) => {
    if (num === null || num === undefined) return '—';
    // Une seule échelle au-delà du milliard : « 4 500 Mds FCFA », jamais « 4.50T » (billion anglo-saxon ambigu).
    const fr = (v: number, d: number) => v.toLocaleString('fr-FR', { minimumFractionDigits: d, maximumFractionDigits: d });
    if (num >= 1e12) return `${fr(num / 1e9, 0)} Mds FCFA`;
    if (num >= 1e9) return `${fr(num / 1e9, 2)} Mds FCFA`;
    if (num >= 1e6) return `${fr(num / 1e6, 2)} M FCFA`;
    return num.toLocaleString('fr-FR') + ' FCFA';
  };

  const renderAISummary = () => (
    <Box>
      <div className='box-title box-vertical-padding box-horizontal-padding'>
        <p>🤖 L'Analyse de l'Action par Nia</p>
      </div>

      {/* AI Persona Tabs */}
      <div
        className='ds-ai-tabs'
        style={{
          display: 'flex',
          borderBottom: '1px solid var(--border-color)',
          background: 'var(--bg-box)',
        }}
      >
        <button
          onClick={() => setAiSummaryType('VALUATION')}
          style={{
            flex: 1,
            padding: '12px',
            border: 'none',
            background: aiSummaryType === 'VALUATION' ? 'var(--bg-hover)' : 'transparent',
            borderBottom: aiSummaryType === 'VALUATION' ? '2px solid #db2777' : 'none',
            color: aiSummaryType === 'VALUATION' ? '#db2777' : 'var(--text-muted)',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          💼 Analyse Fondamentale
        </button>
        <button
          onClick={() => setAiSummaryType('TECHNICAL')}
          style={{
            flex: 1,
            padding: '12px',
            border: 'none',
            background: aiSummaryType === 'TECHNICAL' ? 'var(--bg-hover)' : 'transparent',
            borderBottom: aiSummaryType === 'TECHNICAL' ? '2px solid #7c3aed' : 'none',
            color: aiSummaryType === 'TECHNICAL' ? '#7c3aed' : 'var(--text-muted)',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          📈 Analyse Technique
        </button>
      </div>

      <div
        className='ds-ai-content'
        style={{ padding: '30px', lineHeight: '1.8', color: 'var(--text-main)', fontSize: '16px' }}
      >
        {aiLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}>
              🧠 L'IA analyse les données financières, techniques et dividendes...
            </p>
          </div>
        ) : (
          <>
            {!aiNarrative.includes('❌') && !aiNarrative.includes('⏳') && <DateAvisNia dates={aiNarrativeDates} />}
            <ReactMarkdown>{aiNarrative}</ReactMarkdown>
          </>
        )}

        {/* Info Box about Batch Processing */}
        {!aiLoading && !aiNarrative.includes('❌') && (
          <div
            style={{
              marginTop: '20px',
              padding: '15px',
              background: 'var(--bg-box)',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              fontSize: '14px',
              color: 'var(--text-muted)',
            }}
          >
            ℹ️ Avertissement : Cet avis est généré par un algorithme d'intelligence artificielle à
            des fins éducatives. Il ne constitue pas un conseil personnalisé. L'investissement en
            bourse présente des risques de perte en capital.
          </div>
        )}
      </div>
    </Box>
  );

  const renderPeers = () => {
    if (peersLoading) return <Loader message='Chargement des pairs du secteur...' />;
    if (!peersData || peersData.length === 0) {
      return (
        <Box style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
          <h3>Aucun pair disponible</h3>
          <p>Nous n'avons pas trouvé d'autres sociétés dans le même secteur pour le moment.</p>
        </Box>
      );
    }

    const currentSectorName = stockDetails?.secteur || 'Secteur';

    // Prepare data for Recharts Scatter Plot (ROE vs P/B)
    const scatterData = peersData
      .filter((p: any) => p.roe !== null && p.pb_ratio !== null)
      .map((p: any) => ({
        symbole: p.symbole,
        nom: p.nom_societe || p.symbole,
        roe: parseFloat(p.roe.toFixed(2)),
        pb: parseFloat(p.pb_ratio.toFixed(2)),
        pe: p.pe_ratio ? parseFloat(p.pe_ratio.toFixed(2)) : 'N/A',
        cours: p.cours_actuel,
        size: p.cours_actuel ? Math.min(Math.max(p.cours_actuel, 2000), 20000) : 5000,
        isCurrent: p.symbole === selectedStock,
      }));

    // Calculate sector averages for ReferenceLines
    const roeValues = scatterData.map((d) => d.roe);
    const pbValues = scatterData.map((d) => d.pb);
    const avgRoe =
      roeValues.length > 0 ? roeValues.reduce((a, b) => a + b, 0) / roeValues.length : 0;
    const avgPb = pbValues.length > 0 ? pbValues.reduce((a, b) => a + b, 0) / pbValues.length : 0;

    // Custom Dot Renderer to highlight current stock
    const renderCustomDot = (props: any) => {
      const { cx, cy, payload } = props;
      if (!cx || !cy || !payload) return <g />;
      const fill = payload.isCurrent ? '#4f46e5' : 'var(--text-muted)';
      const stroke = payload.isCurrent ? '#fff' : 'rgba(0,0,0,0.1)';
      const r = payload.isCurrent ? 10 : 6;
      return (
        <g>
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill={fill}
            stroke={stroke}
            strokeWidth={2}
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedStock(payload.symbole)}
          />
          <text
            x={cx}
            y={cy - r - 4}
            textAnchor='middle'
            fill={payload.isCurrent ? '#4f46e5' : 'var(--text-muted)'}
            fontSize={payload.isCurrent ? 12 : 9}
            fontWeight={payload.isCurrent ? 'bold' : 'normal'}
          >
            {payload.symbole}
          </text>
        </g>
      );
    };

    // Custom Tooltip component
    const CustomTooltip = ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
          <div
            style={{
              background: 'var(--bg-box)',
              border: '1px solid var(--border-color)',
              padding: '12px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              fontSize: '13px',
              color: 'var(--text-main)',
            }}
          >
            <strong style={{ color: '#4f46e5' }}>{data.symbole}</strong> - {data.nom}
            <hr style={{ margin: '8px 0', borderColor: 'var(--border-color)' }} />
            <div>
              Cours:{' '}
              <strong>{data.cours ? data.cours.toLocaleString('fr-FR') + ' FCFA' : '—'}</strong>
            </div>
            <div>
              P/B Ratio: <strong>{data.pb}x</strong>
            </div>
            <div>
              ROE: <strong>{data.roe}%</strong>
            </div>
            <div>
              P/E Ratio: <strong>{data.pe}x</strong>
            </div>
          </div>
        );
      }
      return <></>;
    };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Graphique de dispersion */}
        {scatterData.length > 0 && (
          <Box style={{ padding: '20px' }}>
            <div
              className='box-title box-vertical-padding box-horizontal-padding'
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <p>📈 Analyse Relative : ROE vs Price-to-Book (P/B)</p>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Secteur : {currentSectorName}
              </span>
            </div>

            <div style={{ height: '350px', width: '100%', marginTop: '20px' }}>
              <ResponsiveContainer width='100%' height='100%'>
                <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 10 }}>
                  <CartesianGrid strokeDasharray='3 3' stroke='var(--border-color)' opacity={0.5} />
                  <XAxis
                    type='number'
                    dataKey='roe'
                    name='ROE'
                    unit='%'
                    stroke='var(--text-muted)'
                    fontSize={11}
                    domain={['auto', 'auto']}
                  >
                    <Label
                      value='Rentabilité financière (ROE %)'
                      offset={-10}
                      position='insideBottom'
                      fill='var(--text-muted)'
                      fontSize={12}
                    />
                  </XAxis>
                  <YAxis
                    type='number'
                    dataKey='pb'
                    name='P/B'
                    unit='x'
                    stroke='var(--text-muted)'
                    fontSize={11}
                    domain={['auto', 'auto']}
                  >
                    <Label
                      value='Multiple de valeur comptable (P/B)'
                      angle={-90}
                      position='insideLeft'
                      style={{ textAnchor: 'middle' }}
                      fill='var(--text-muted)'
                      fontSize={12}
                    />
                  </YAxis>
                  <ZAxis type='number' dataKey='size' range={[60, 400]} />
                  <Tooltip content={<CustomTooltip />} />

                  {/* Reference lines dividing into quadrants */}
                  <ReferenceLine
                    x={avgRoe}
                    stroke='#f59e0b'
                    strokeDasharray='3 3'
                    label={{
                      value: `Moy. ROE (${avgRoe.toFixed(1)}%)`,
                      fill: '#f59e0b',
                      fontSize: 10,
                      position: 'top',
                    }}
                  />
                  <ReferenceLine
                    y={avgPb}
                    stroke='#f59e0b'
                    strokeDasharray='3 3'
                    label={{
                      value: `Moy. P/B (${avgPb.toFixed(2)}x)`,
                      fill: '#f59e0b',
                      fontSize: 10,
                      position: 'right',
                    }}
                  />

                  <Scatter name='Sociétés' data={scatterData} shape={renderCustomDot} />
                </ScatterChart>
              </ResponsiveContainer>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '15px',
                marginTop: '15px',
                padding: '10px',
                background: 'var(--bg-input)',
                borderRadius: '8px',
                fontSize: '11px',
                color: 'var(--text-muted)',
              }}
            >
              <div>
                🟢 <strong>Quadrant Bas-Droite (Pépites relatives) :</strong> Rentabilité (ROE) supérieure et valorisation (P/B) inférieure aux moyennes sectorielles ➡️ <strong>Sous-évaluation sectorielle relative</strong> (à croiser avec l'Indice de Valeur Kanea global).
              </div>
              <div>
                🔴 <strong>Quadrant Haut-Gauche (Surcote relative) :</strong> Rentabilité (ROE) inférieure et valorisation (P/B) supérieure aux moyennes sectorielles ➡️ <strong>Surévaluation sectorielle relative</strong> (qui peut toutefois être justifiée par d'autres modèles de croissance).
              </div>
            </div>
          </Box>
        )}

        {/* Tableau comparatif */}
        <Box style={{ padding: '20px', overflowX: 'auto' }}>
          <div className='box-title box-vertical-padding box-horizontal-padding'>
            <p>📋 Tableau Comparatif des Concurrents</p>
          </div>

          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              marginTop: '15px',
              fontSize: '14px',
              textAlign: 'left',
              minWidth: '800px',
            }}
          >
            <thead>
              <tr
                style={{
                  borderBottom: '2px solid var(--border-color)',
                  color: 'var(--text-muted)',
                  fontWeight: 'bold',
                }}
              >
                <th style={{ padding: '12px 8px' }}>Action</th>
                <th style={{ padding: '12px 8px' }}>Société</th>
                <th style={{ padding: '12px 8px', textAlign: 'right' }}>Cours (FCFA)</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>P/E Ratio</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>P/B Ratio</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>P/S Ratio</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>ROE (%)</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>Marge Nette (%)</th>
                <th style={{ padding: '12px 8px', textAlign: 'center' }}>Rendement (%)</th>
              </tr>
            </thead>
            <tbody>
              {peersData.map((peer: any) => {
                const isCurrent = peer.symbole === selectedStock;
                return (
                  <tr
                    key={peer.symbole}
                    style={{
                      borderBottom: '1px solid var(--border-color)',
                      background: isCurrent ? 'rgba(102, 126, 234, 0.12)' : 'transparent',
                      fontWeight: isCurrent ? 'bold' : 'normal',
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedStock(peer.symbole)}
                  >
                    <td style={{ padding: '12px 8px', color: '#4f46e5', fontWeight: 'bold' }}>
                      {peer.symbole}
                    </td>
                    <td
                      style={{
                        padding: '12px 8px',
                        color: 'var(--text-main)',
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {peer.nom_societe || '—'}
                    </td>
                    <td
                      style={{ padding: '12px 8px', textAlign: 'right', color: 'var(--text-main)' }}
                    >
                      {peer.cours_actuel ? peer.cours_actuel.toLocaleString('fr-FR') : '—'}
                    </td>
                    <td
                      style={{
                        padding: '12px 8px',
                        textAlign: 'center',
                        color: 'var(--text-main)',
                      }}
                    >
                      {peer.pe_ratio ? `${peer.pe_ratio.toFixed(2)}x` : '—'}
                    </td>
                    <td
                      style={{
                        padding: '12px 8px',
                        textAlign: 'center',
                        color: 'var(--text-main)',
                      }}
                    >
                      {peer.pb_ratio ? `${peer.pb_ratio.toFixed(2)}x` : '—'}
                    </td>
                    <td
                      style={{
                        padding: '12px 8px',
                        textAlign: 'center',
                        color: 'var(--text-main)',
                      }}
                    >
                      {peer.ps_ratio ? `${peer.ps_ratio.toFixed(2)}x` : '—'}
                    </td>
                    <td
                      style={{
                        padding: '12px 8px',
                        textAlign: 'center',
                        color:
                          peer.roe > 0 ? '#10b981' : peer.roe < 0 ? '#ef4444' : 'var(--text-main)',
                      }}
                    >
                      {peer.roe ? `${peer.roe.toFixed(2)}%` : '—'}
                    </td>
                    <td
                      style={{
                        padding: '12px 8px',
                        textAlign: 'center',
                        color:
                          peer.net_margin > 0
                            ? '#10b981'
                            : peer.net_margin < 0
                              ? '#ef4444'
                              : 'var(--text-main)',
                      }}
                    >
                      {peer.net_margin ? `${peer.net_margin.toFixed(2)}%` : '—'}
                    </td>
                    <td
                      style={{
                        padding: '12px 8px',
                        textAlign: 'center',
                        color: '#10b981',
                        fontWeight: 'bold',
                      }}
                    >
                      {peer.dividend_yield ? `${peer.dividend_yield.toFixed(2)}%` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </div>
    );
  };

  const renderAnalysis = () => {
    if (loading && chartData.length === 0) return <Loader message="Chargement de l'analyse..." />;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* TOOLBAR */}
        <Box>
          <div
            className='ds-chart-toolbar'
            style={{
              padding: '15px',
              display: 'flex',
              flexWrap: 'nowrap',
              gap: '15px',
              alignItems: 'center',
              overflowX: 'auto',
              overflowY: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {/* Period Selector */}
            <div style={{ display: 'flex', gap: '5px' }}>
              {CHART_PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setChartPeriod(opt.value)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '4px',
                    border:
                      chartPeriod === opt.value
                        ? '1px solid #3b82f6'
                        : '1px solid var(--border-color)',
                    background:
                      chartPeriod === opt.value ? 'rgba(59, 130, 246, 0.1)' : 'var(--bg-box)',
                    color: chartPeriod === opt.value ? '#3b82f6' : 'var(--text-main)',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                  }}
                >
                  {opt.value}
                </button>
              ))}
            </div>

            <div style={{ width: '1px', height: '24px', background: '#e0e0e0' }}></div>

            {/* Type Selector */}
            <select
              value={chartType}
              onChange={(e) => {
                const newType = e.target.value as ChartType;
                setChartType(newType);
                trackAction('CHANGE_CHART_TYPE');
              }}
              style={{
                padding: '6px 10px',
                borderRadius: '4px',
                border: '1px solid var(--border-color)',
                background: 'var(--bg-box)',
                color: 'var(--text-main)',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              <option value='candlestick'>🕯️ Bougies</option>
              <option value='line'>📈 Ligne</option>
              <option value='heikin-ashi'>📊 Heikin-Ashi</option>
              <option value='area'>⛰️ Aire</option>
            </select>

            <div style={{ width: '1px', height: '24px', background: '#e0e0e0' }}></div>

            {/* Overlays Toggles */}
            <div
              className='ds-overlays'
              style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
                fontSize: '13px',
                flexWrap: 'nowrap',
              }}
            >
              <div
                onClick={() => setOverlays({ ...overlays, volume: !overlays.volume })}
                style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  border: overlays.volume ? '1px solid #26a69a' : '1px solid var(--border-color)',
                  background: overlays.volume ? 'rgba(38, 166, 154, 0.15)' : 'var(--bg-box)',
                  color: overlays.volume ? '#26a69a' : 'var(--text-muted)',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {overlays.volume ? '✅' : '⚪'} Volume
              </div>

              <div
                onClick={() => setOverlays({ ...overlays, sma20: !overlays.sma20 })}
                style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  border: overlays.sma20 ? '1px solid #ff9800' : '1px solid var(--border-color)',
                  background: overlays.sma20 ? 'rgba(255, 152, 0, 0.15)' : 'var(--bg-box)',
                  color: overlays.sma20 ? '#ff9800' : 'var(--text-muted)',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {overlays.sma20 ? '✅' : '⚪'} SMA 20
              </div>

              <div
                onClick={() => setOverlays({ ...overlays, sma50: !overlays.sma50 })}
                style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  border: overlays.sma50 ? '1px solid #00C853' : '1px solid var(--border-color)',
                  background: overlays.sma50 ? 'rgba(0, 200, 83, 0.15)' : 'var(--bg-box)',
                  color: overlays.sma50 ? '#00c853' : 'var(--text-muted)',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {overlays.sma50 ? '✅' : '⚪'} SMA 50
              </div>

              <div
                onClick={() => setOverlays({ ...overlays, sma200: !overlays.sma200 })}
                style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  border: overlays.sma200 ? '1px solid #f44336' : '1px solid var(--border-color)',
                  background: overlays.sma200 ? 'rgba(244, 67, 54, 0.15)' : 'var(--bg-box)',
                  color: overlays.sma200 ? '#f44336' : 'var(--text-muted)',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {overlays.sma200 ? '✅' : '⚪'} SMA 200
              </div>

              <div
                onClick={() => setOverlays({ ...overlays, bollinger: !overlays.bollinger })}
                style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  border: overlays.bollinger
                    ? '1px solid #7c3aed'
                    : '1px solid var(--border-color)',
                  background: overlays.bollinger ? 'rgba(124, 58, 237, 0.15)' : 'var(--bg-box)',
                  color: overlays.bollinger ? '#7c3aed' : 'var(--text-muted)',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {overlays.bollinger ? '✅' : '⚪'} Bollinger
              </div>

              <div
                onClick={() => setAfficherMarqueurs(!afficherMarqueurs)}
                title="Repères des fractionnements et augmentations de capital, pour vérifier la continuité de la série ajustée"
                style={{
                  padding: '6px 12px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  border: afficherMarqueurs
                    ? '1px solid #0891b2'
                    : '1px solid var(--border-color)',
                  background: afficherMarqueurs ? 'rgba(8, 145, 178, 0.15)' : 'var(--bg-box)',
                  color: afficherMarqueurs ? '#0891b2' : 'var(--text-muted)',
                  fontWeight: '600',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {afficherMarqueurs ? '✅' : '⚪'} Opérations
                {afficherMarqueurs && marqueursEvenements.length > 0 && (
                  <span style={{ opacity: 0.7, fontWeight: 400 }}>
                    {marqueursVisibles.length === marqueursEvenements.length
                      ? `(${marqueursEvenements.length})`
                      : `(${marqueursVisibles.length}/${marqueursEvenements.length})`}
                  </span>
                )}
              </div>
            </div>

            <div style={{ width: '1px', height: '24px', background: '#e0e0e0' }}></div>
          </div>
        </Box>

        {/* MAIN CHART */}
        <Box>
          {afficherMarqueurs &&
            marqueursEvenements.length > 0 &&
            marqueursVisibles.length === 0 && (
              <div
                style={{
                  margin: '0 0 8px 0',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  background: 'rgba(8, 145, 178, 0.08)',
                  border: '1px solid rgba(8, 145, 178, 0.25)',
                  color: 'var(--text-muted)',
                  fontSize: '12px',
                }}
              >
                {marqueursEvenements.length} opération
                {marqueursEvenements.length > 1 ? 's' : ''} sur titres pour cette valeur,
                toutes antérieures à la période affichée
                {' — '}
                {marqueursEvenements
                  .map((m) => `${m.date_effective} ${m.libelle}`)
                  .join(' · ')}
                . Élargissez la période pour les voir.
              </div>
            )}
          <div style={{ padding: '0px' }}>
            <AdvancedChart
              data={chartData} // Always OHLC (Generic)
              marqueurs={afficherMarqueurs ? marqueursVisibles : undefined}
              volumeData={volumeData}
              overlayData={{
                sma20: indicatorData?.sma20.filter((d) => d.time >= (chartData[0]?.time as string)),
                sma50: indicatorData?.sma50.filter((d) => d.time >= (chartData[0]?.time as string)),
                sma200: indicatorData?.sma200.filter(
                  (d) => d.time >= (chartData[0]?.time as string)
                ),
                bollinger: indicatorData?.bollinger
                  ? {
                      upper: indicatorData.bollinger.upper.filter(
                        (d) => d.time >= (chartData[0]?.time as string)
                      ),
                      lower: indicatorData.bollinger.lower.filter(
                        (d) => d.time >= (chartData[0]?.time as string)
                      ),
                      sma: indicatorData.bollinger.sma.filter(
                        (d) => d.time >= (chartData[0]?.time as string)
                      ),
                    }
                  : undefined,
              }}
              chartType={chartType}
              overlays={overlays}
              height={isMobile ? Math.max(400, Math.round(window.innerHeight * 0.65)) : 500}
            />
          </div>
        </Box>

        {/* AUTOMATED ANALYSIS TEXT */}
        <Box>
          <div className='box-title box-vertical-padding box-horizontal-padding'>
            <p>🤖 Analyse Technique Automatisée</p>
          </div>
          <div
            className='ds-analysis-text'
            style={{ padding: '20px', lineHeight: '1.6', color: 'var(--text-main)' }}
          >
            {marketAnalysis ? <p>{marketAnalysis}</p> : <p>Analyse en cours...</p>}
          </div>
        </Box>

        {/* OSCILLATORS (Always Visible) */}
        {indicatorData && (
          <Box>
            <div
              style={{
                padding: '15px 20px',
                borderBottom: '1px solid var(--border-color)',
                fontWeight: 'bold',
                color: 'var(--text-heading)',
              }}
            >
              RSI (14)
            </div>
            <IndicatorChart
              height={250}
              fileName={`rsi_${selectedStock}`}
              series={[
                {
                  type: 'line',
                  data: indicatorData.rsi,
                  options: { color: '#7E57C2', lineWidth: 2 },
                },
              ]}
              colors={{ gridColor: 'rgba(156, 163, 175, 0.1)', textColor: 'var(--text-heading)' }}
            />
          </Box>
        )}

        {indicatorData && (
          <Box>
            <div
              style={{
                padding: '15px 20px',
                borderBottom: '1px solid var(--border-color)',
                fontWeight: 'bold',
                color: 'var(--text-heading)',
              }}
            >
              MACD (12, 26, 9)
            </div>
            <IndicatorChart
              height={250}
              fileName={`macd_${selectedStock}`}
              series={[
                { type: 'histogram', data: indicatorData.macd.histogram },
                {
                  type: 'line',
                  data: indicatorData.macd.macdLine,
                  options: { color: '#2962FF', lineWidth: 2 },
                },
                {
                  type: 'line',
                  data: indicatorData.macd.signalLine,
                  options: { color: '#FF6D00', lineWidth: 2 },
                },
              ]}
              colors={{ gridColor: 'rgba(156, 163, 175, 0.1)', textColor: 'var(--text-heading)' }}
            />
          </Box>
        )}
      </div>
    );
  };

  const renderDividends = () => {
    if (!dividendData || dividendData.length === 0) {
      return (
        <Box>
          <div style={{ padding: '50px', textAlign: 'center' }}>
            <p>Aucune donnée de dividende disponible pour {selectedStock}.</p>
          </div>
        </Box>
      );
    }

    // statut='AUCUN' (décision explicite de non-distribution, cf. CLAUDE.md "Chantier
    // Dividendes") n'a pas de date_paiement — lightweight-charts plante sur time: null (observé
    // en conditions réelles sur FTSC 2025, session du 2026-07-18). Exclu du graphique/analyse
    // (rien à tracer sur un axe temporel sans date), mais reste visible dans le tableau ci-dessous.
    const chartableDividends = dividendData.filter((d) => d.statut !== 'AUCUN' && d.date_paiement);

    // Prepare data for chart — sort by date ascending
    const sortedDividends = [...chartableDividends].sort(
      (a, b) => new Date(a.date_paiement as string).getTime() - new Date(b.date_paiement as string).getTime()
    );

    // Graphique et analyse portent sur le BRUT. Une ligne sans brut connu est écartée
    // plutôt que rabattue sur son net : mélanger les deux sur un même axe fabriquerait
    // une baisse apparente de 3 à 12,5 % qui n'a jamais eu lieu.
    const dividendsAvecBrut = sortedDividends.filter(
      (d) => getDividendeMontantBrut(d) !== null
    );

    const dividendsChartData = dividendsAvecBrut.map((d) => ({
      time: d.date_paiement as string,
      value: getDividendeMontantBrut(d) as number,
      color: '#00b638',
    }));

    const amounts = dividendsAvecBrut.map((d) => getDividendeMontantBrut(d) as number);
    const years = dividendsAvecBrut.map((d) => getDividendeYear(d)).filter((y) => y > 0);

    const lastDividend = amounts.length > 0 ? amounts[amounts.length - 1] : 0;
    const prevDividend = amounts.length >= 2 ? amounts[amounts.length - 2] : null;
    const avgDividend = amounts.length > 0 ? amounts.reduce((a, b) => a + b, 0) / amounts.length : 0;

    const lastExercice =
      years.length > 0
        ? years[years.length - 1]
        : null;
    const prevExercice =
      years.length >= 2
        ? years[years.length - 2]
        : null;

    // Variation N vs N-1 (YoY) : robuste et non biaisée par les anciens fractionnements
    const yoyGrowth =
      prevDividend !== null && prevDividend > 0
        ? ((lastDividend - prevDividend) / prevDividend) * 100
        : null;

    let currentPrice = 0;
    if (chartData && chartData.length > 0) {
      const lastItem = chartData[chartData.length - 1] as any;
      currentPrice = lastItem.close || lastItem.value || 0;
    } else if (quotations && quotations.length > 0) {
      currentPrice = quotations[0]?.cours_cloture || 0;
    } else if (capitalisation?.cours_cloture) {
      currentPrice = capitalisation.cours_cloture;
    }

    const dividendYield = currentPrice > 0 && lastDividend > 0 ? (lastDividend / currentPrice) * 100 : 0;

    let increases = 0;
    for (let i = 1; i < amounts.length; i++) {
      if (amounts[i] >= amounts[i - 1]) increases++;
    }
    const stabilityScore =
      amounts.length > 1
        ? Math.round((increases / (amounts.length - 1)) * 100)
        : amounts.length === 1
          ? 100
          : 0;

    const kpiSummary = (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: '14px',
        }}
      >
        {/* Card 1: Dernier Dividende Brut */}
        <div
          style={{
            background: 'var(--bg-box)',
            border: '1px solid var(--border-color)',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            boxShadow: '0 1px 4px var(--shadow-color)',
          }}
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              background: 'rgba(16, 185, 129, 0.12)',
              color: '#059669',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <i className='material-icons' style={{ fontSize: '24px' }}>
              payments
            </i>
          </div>
          <div>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Dernier Dividende Brut
            </div>
            <div
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: 'var(--text-heading)',
                marginTop: '2px',
              }}
            >
              {lastDividend > 0 ? `${lastDividend.toLocaleString('fr-FR')} FCFA` : '—'}
            </div>
            {lastExercice && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                Exercice {lastExercice}
              </div>
            )}
          </div>
        </div>

        {/* Card 2: Rendement Brut (Yield) */}
        <div
          style={{
            background: 'var(--bg-box)',
            border: '1px solid var(--border-color)',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            boxShadow: '0 1px 4px var(--shadow-color)',
          }}
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              background: 'rgba(59, 130, 246, 0.12)',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <i className='material-icons' style={{ fontSize: '24px' }}>
              percent
            </i>
          </div>
          <div>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Rendement Brut (Yield)
            </div>
            <div
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#2563eb',
                marginTop: '2px',
              }}
            >
              {dividendYield > 0 ? `${dividendYield.toFixed(2)} %` : '—'}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
              Sur cours de {currentPrice > 0 ? `${currentPrice.toLocaleString('fr-FR')} FCFA` : '—'}
            </div>
          </div>
        </div>

        {/* Card 3: Évolution vs N-1 (YoY) */}
        <div
          style={{
            background: 'var(--bg-box)',
            border: '1px solid var(--border-color)',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            boxShadow: '0 1px 4px var(--shadow-color)',
          }}
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              background:
                yoyGrowth === null
                  ? 'rgba(100, 116, 139, 0.12)'
                  : yoyGrowth >= 0
                    ? 'rgba(16, 185, 129, 0.12)'
                    : 'rgba(239, 68, 68, 0.12)',
              color:
                yoyGrowth === null
                  ? '#64748b'
                  : yoyGrowth >= 0
                    ? '#059669'
                    : '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <i className='material-icons' style={{ fontSize: '24px' }}>
              {yoyGrowth === null
                ? 'horizontal_rule'
                : yoyGrowth > 0
                  ? 'trending_up'
                  : yoyGrowth < 0
                    ? 'trending_down'
                    : 'trending_flat'}
            </i>
          </div>
          <div>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Évolution vs N-1
            </div>
            <div
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color:
                  yoyGrowth === null
                    ? 'var(--text-heading)'
                    : yoyGrowth >= 0
                      ? '#059669'
                      : '#dc2626',
                marginTop: '2px',
              }}
            >
              {yoyGrowth !== null
                ? `${yoyGrowth >= 0 ? '+' : ''}${yoyGrowth.toFixed(1)} %`
                : '1er historique'}
            </div>
            {prevExercice && prevDividend !== null && (
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                vs {prevDividend.toLocaleString('fr-FR')} F ({prevExercice})
              </div>
            )}
          </div>
        </div>

        {/* Card 4: Régularité / Stabilité */}
        <div
          style={{
            background: 'var(--bg-box)',
            border: '1px solid var(--border-color)',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            boxShadow: '0 1px 4px var(--shadow-color)',
          }}
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              background:
                stabilityScore >= 70
                  ? 'rgba(16, 185, 129, 0.12)'
                  : stabilityScore >= 40
                    ? 'rgba(245, 158, 11, 0.12)'
                    : 'rgba(239, 68, 68, 0.12)',
              color:
                stabilityScore >= 70
                  ? '#059669'
                  : stabilityScore >= 40
                    ? '#d97706'
                    : '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <i className='material-icons' style={{ fontSize: '24px' }}>
              verified
            </i>
          </div>
          <div>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Régularité Dividende
            </div>
            <div
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: 'var(--text-heading)',
                marginTop: '2px',
              }}
            >
              {stabilityScore} %
            </div>
            <div
              style={{
                fontSize: '11px',
                color:
                  stabilityScore >= 70
                    ? '#059669'
                    : stabilityScore >= 40
                      ? '#d97706'
                      : '#dc2626',
                marginTop: '2px',
                fontWeight: 600,
              }}
            >
              {stabilityScore >= 70
                ? 'Politique régulière'
                : stabilityScore >= 40
                  ? 'Politique variable'
                  : 'Forte variabilité'}
            </div>
          </div>
        </div>

        {/* Card 5: Dividende Brut Moyen */}
        <div
          style={{
            background: 'var(--bg-box)',
            border: '1px solid var(--border-color)',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            boxShadow: '0 1px 4px var(--shadow-color)',
          }}
        >
          <div
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              background: 'rgba(139, 92, 246, 0.12)',
              color: '#7c3aed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <i className='material-icons' style={{ fontSize: '24px' }}>
              query_stats
            </i>
          </div>
          <div>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              Moyenne Historique
            </div>
            <div
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: 'var(--text-heading)',
                marginTop: '2px',
              }}
            >
              {avgDividend > 0 ? `${Math.round(avgDividend).toLocaleString('fr-FR')} FCFA` : '—'}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
              Sur {amounts.length} versement{amounts.length > 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>
    );

    if (isMobile) {
      return (
        <div
          className='ds-dividends'
          style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
        >
          {/* KPI Summary Cards */}
          {kpiSummary}

          {/* Graphique Mobile */}
          <div
            style={{
              background: 'var(--bg-box)',
              borderRadius: '12px',
              padding: '16px',
              border: '1px solid var(--border-color)',
              boxShadow: '0 1px 6px -2px var(--shadow-color)',
            }}
          >
            <h3 style={{ margin: '0 0 12px', fontSize: '15px', color: 'var(--text-heading)' }}>
              Historique des Dividendes Bruts
            </h3>
            <IndicatorChart
              height={220}
              fileName={`dividendes_${selectedStock}`}
              series={[
                { type: 'histogram', data: dividendsChartData, options: { color: '#00b638' } },
              ]}
            />
          </div>

          {/* Liste Mobile */}
          <div
            style={{
              background: 'var(--bg-box)',
              borderRadius: '12px',
              border: '1px solid var(--border-color)',
              overflow: 'hidden',
              boxShadow: '0 1px 6px -2px var(--shadow-color)',
            }}
          >
            <div
              style={{
                padding: '14px 16px',
                background: 'var(--bg-hover)',
                borderBottom: '1px solid var(--border-color)',
                fontWeight: '600',
                fontSize: '14px',
                color: 'var(--text-heading)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Derniers détachements</span>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 500 }}>
                {dividendData.length} exercice{dividendData.length > 1 ? 's' : ''}
              </span>
            </div>
            <div style={{ padding: '0 16px' }}>
              {[...dividendData]
                .sort(parPaiementDecroissant)
                .map((div, index) => {
                  const brut = getDividendeMontantBrut(div);
                  return (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '14px 0',
                        borderBottom:
                          index < dividendData.length - 1 ? '1px solid var(--border-color)' : 'none',
                        fontSize: '14px',
                      }}
                    >
                      {div.statut === 'AUCUN' ? (
                        <>
                          <div>
                            <div style={{ color: 'var(--text-main)', fontWeight: '600' }}>
                              Exercice {div.exercice ?? '—'}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                              Non distribué
                            </div>
                          </div>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: 600,
                              background: '#fee2e2',
                              color: '#991b1b',
                            }}
                          >
                            Aucun dividende
                          </span>
                        </>
                      ) : (
                        <>
                          <div>
                            <div style={{ color: 'var(--text-main)', fontWeight: '600' }}>
                              {div.exercice ? `Exercice ${div.exercice}` : (div.date_paiement ?? '—')}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                              <i className='material-icons' style={{ fontSize: '12px' }}>event</i>
                              {div.date_paiement ?? '—'}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ color: '#059669', fontWeight: 'bold', fontSize: '15px' }}>
                              {brut !== null ? `${brut.toLocaleString('fr-FR')} FCFA` : '—'}
                            </div>
                            <span
                              style={{
                                display: 'inline-block',
                                padding: '1px 6px',
                                borderRadius: '8px',
                                fontSize: '10px',
                                fontWeight: 600,
                                background: '#ecfdf5',
                                color: '#065f46',
                                border: '1px solid #a7f3d0',
                              }}
                            >
                              Payé
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className='ds-dividends'
        style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}
      >
        {/* KPI Summary Cards */}
        {kpiSummary}

        {/* Graphique Historique */}
        <Box>
          <div style={{ padding: '20px' }}>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3
                style={{ margin: 0, fontSize: '16px', color: 'var(--text-heading)' }}
              >{`Historique des Dividendes Bruts - ${selectedStock}`}</h3>
              {years.length > 1 && (
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 600,
                    padding: '3px 10px',
                    borderRadius: '12px',
                    background: 'rgba(59, 130, 246, 0.1)',
                    color: '#2563eb',
                  }}
                >
                  {years[0]} – {years[years.length - 1]}
                </span>
              )}
            </div>
            <IndicatorChart
              height={380}
              fileName={`dividendes_${selectedStock}`}
              series={[
                { type: 'histogram', data: dividendsChartData, options: { color: '#059669' } },
              ]}
            />
          </div>
        </Box>

        {/* Tableau des Dividendes */}
        <Box>
          <div
            className='box-title box-vertical-padding box-horizontal-padding'
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <p>Historique des Détachements & Dividendes</p>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 500 }}>
              {dividendData.length} exercice{dividendData.length > 1 ? 's' : ''} répertorié{dividendData.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className='box-content-height-nobutton' style={{ overflowX: 'auto' }}>
            <table className='data-table'>
              <thead>
                <tr>
                  <th className='left' style={{ width: '180px' }}>Date de Paiement</th>
                  <th className='left' style={{ width: '140px' }}>Exercice</th>
                  <th className='right' style={{ width: '200px' }}>Montant Brut (FCFA)</th>
                  <th className='right' style={{ width: '140px' }}>Statut</th>
                </tr>
              </thead>
              <tbody>
                {[...dividendData]
                  .sort(parPaiementDecroissant)
                  .map((div, index) => {
                    const brut = getDividendeMontantBrut(div);
                    return div.statut === 'AUCUN' ? (
                      <tr key={index}>
                        <td className='left' style={{ color: 'var(--text-muted)' }}>
                          —
                        </td>
                        <td className='left'>
                          <span style={{ fontWeight: 600, color: 'var(--text-heading)' }}>
                            Exercice {div.exercice ?? '—'}
                          </span>
                        </td>
                        <td
                          className='right'
                          style={{ fontStyle: 'italic', color: 'var(--text-muted)' }}
                        >
                          0 FCFA
                        </td>
                        <td className='right'>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: 600,
                              background: '#fee2e2',
                              color: '#991b1b',
                            }}
                          >
                            Aucun dividende
                          </span>
                        </td>
                      </tr>
                    ) : (
                      <tr key={index}>
                        <td className='left' style={{ fontWeight: 500 }}>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              color: 'var(--text-main)',
                            }}
                          >
                            <i
                              className='material-icons'
                              style={{ fontSize: '14px', color: 'var(--text-muted)' }}
                            >
                              event
                            </i>
                            {div.date_paiement ?? '—'}
                          </span>
                        </td>
                        <td className='left'>
                          <span style={{ fontWeight: 600, color: 'var(--text-heading)' }}>
                            {div.exercice ? `Exercice ${div.exercice}` : '—'}
                          </span>
                        </td>
                        <td
                          className='right'
                          style={{ fontWeight: 700, color: '#059669', fontSize: '14px' }}
                        >
                          {brut !== null ? `${brut.toLocaleString('fr-FR')} FCFA` : '—'}
                        </td>
                        <td className='right'>
                          <span
                            style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: 600,
                              background: '#ecfdf5',
                              color: '#065f46',
                              border: '1px solid #a7f3d0',
                            }}
                          >
                            Payé
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </Box>
      </div>
    );
  };

  const renderValuation = () => {
    if (valuationLoading) {
      return <Loader message='Calcul de la juste valeur en cours...' />;
    }

    if (
      !valuationData ||
      (valuationData.nb_modeles_valides === 0 &&
        (!valuationData.synthesis || valuationData.synthesis.nb_modeles_valides === 0))
    ) {
      return (
        <Box>
          <div style={{ padding: '50px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>📊</div>
            <h3 style={{ margin: '0 0 15px', color: 'var(--text-heading)' }}>
              Aucun modèle de valorisation applicable
            </h3>
            <p
              style={{
                fontSize: '14px',
                color: 'var(--text-muted)',
                marginTop: '10px',
                maxWidth: '500px',
                margin: '0 auto',
              }}
            >
              Pour {selectedStock}, les données nécessaires ne sont pas disponibles :
            </p>
            <div
              style={{
                marginTop: '20px',
                padding: '20px',
                background: 'var(--bg-box)',
                border: '1px solid var(--border-color)',
                borderRadius: '10px',
                textAlign: 'left',
                maxWidth: '500px',
                margin: '20px auto',
              }}
            >
              <p style={{ margin: '0 0 10px', fontWeight: 'bold' }}>Données requises :</p>
              <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--text-muted)' }}>
                <li>
                  📈 <strong>Dividendes</strong> : historique des distributions (modèles DDM, Yield
                  Reversion)
                </li>
                <li>
                  📊 <strong>Fondamentaux</strong> : résultat net, equity, EBITDA (modèles P/E, P/B,
                  Graham)
                </li>
              </ul>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '15px' }}>
              Les données fondamentales peuvent être ajoutées via l'administration.
            </p>
          </div>
        </Box>
      );
    }

    const stockInfo = stocks.find((s) => s.symbole === selectedStock);
    const getRecommendationEmoji = (rec: string) => {
      if (rec === 'SOUS-ÉVALUÉE') return '🚀';
      if (rec === 'SUR-ÉVALUÉE') return '⚠️';
      return '➡️';
    };

    const getReliabilityColor = (fiab: string) => {
      if (!fiab) return 'var(--text-muted)';
      const normalized = fiab.toUpperCase();
      if (normalized === 'ÉLEVÉE' || normalized === 'ELEVEE') return '#10b981'; // Green
      if (normalized === 'MOYENNE') return '#f59e0b'; // Orange
      if (normalized === 'FAIBLE') return '#ef4444'; // Red
      if (normalized === 'SPÉCULATIVE' || normalized === 'SPECULATIVE') return '#fbbf24'; // Bright Orange/Amber
      return 'var(--text-muted)';
    };

    const formatPrice = (price: number | null | undefined) => {
      if (price === null || price === undefined) return '—';
      return price.toLocaleString('fr-FR') + ' FCFA';
    };

    const getPriceCardStyle = (
      fairPrice: number | null | undefined,
      currentPrice: number | null | undefined
    ) => {
      if (!fairPrice || !currentPrice)
        return { borderColor: 'var(--border-color)', bgColor: 'var(--bg-box)' };
      const diff = ((fairPrice - currentPrice) / currentPrice) * 100;
      if (diff > 10) return { borderColor: '#10b981', bgColor: 'rgba(16, 185, 129, 0.1)' };
      if (diff < -10) return { borderColor: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)' };
      return { borderColor: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.1)' };
    };

    if (!valuationData) {
      return (
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            background: 'var(--bg-box)',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            margin: '20px 0',
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <h3 style={{ color: 'var(--text-heading)', marginBottom: '10px' }}>
            Valorisation en attente
          </h3>
          <p
            style={{
              color: 'var(--text-muted)',
              lineHeight: '1.6',
              maxWidth: '400px',
              margin: '0 auto',
            }}
          >
            Les calculs de valorisation pour <strong>{selectedStock}</strong> ne sont pas encore
            disponibles dans notre cache. Ils seront générés automatiquement lors de la prochaine
            mise à jour quotidienne.
          </p>
        </div>
      );
    }

    // Dates des données prises en compte : séance du calcul et comptes utilisés par les multiples.
    const valuationDates: string[] = (() => {
      const parts: string[] = [];
      const fmt = (iso: string) =>
        new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        });
      if (valuationData.date_calcul) {
        parts.push(`Valorisation calculée sur la séance du ${fmt(valuationData.date_calcul)}`);
      }
      const multiples: any = valuationData.market_multiples || marketMultiplesData || {};
      const comptes: any = Object.values(multiples).find(
        (m: any) => m && typeof m === 'object' && /^\d{4}-\d{2}-\d{2}/.test(String(m.closing_date))
      );
      if (comptes) {
        const periodes: Record<string, string> = {
          ANNUAL: 'annuels',
          S1: 'semestriels (S1)',
          Q1: 'du 1er trimestre',
          Q3: 'du 3e trimestre',
          '9M': 'à 9 mois',
        };
        const periode = periodes[comptes.period_type] ? ` ${periodes[comptes.period_type]}` : '';
        parts.push(`Comptes${periode} clos le ${fmt(String(comptes.closing_date))}`);
      }
      return parts;
    })();

    if (isMobile) {
      const synth = valuationData.synthesis || valuationData;

      const renderMobileModelCard = (title: string, icon: string, model: any) => {
        if (!model) return null;
        const style = getPriceCardStyle(
          model.prix_juste || model.prix_cible,
          valuationData.cours_actuel
        );
        const price = model.prix_juste || model.prix_cible;
        if (!price) return null;

        return (
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--border-color)',
              borderLeft: `4px solid ${style.borderColor}`,
              background: style.bgColor,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '4px',
              }}
            >
              <span style={{ fontWeight: '600', color: 'var(--text-heading)', fontSize: '14px' }}>
                {icon} {title}
              </span>
              <span style={{ fontWeight: 'bold', color: 'var(--text-heading)', fontSize: '16px' }}>
                {formatPrice(price)}
              </span>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              {model.interpretation}
            </div>
          </div>
        );
      };

      return (
        <div
          className='ds-valuation'
          style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingBottom: '20px' }}
        >
          {/* Main Hero Card */}
          <div
            style={{
              background: 'var(--bg-box)',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid var(--border-color)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '20px',
                marginBottom: '16px',
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: '11px',
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Cours Actuel
                </div>
                <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--text-heading)' }}>
                  {formatPrice(valuationData.cours_actuel)}
                </div>
              </div>
              <div
                style={{
                  width: '1px',
                  background: 'var(--border-color)',
                  height: '30px',
                  alignSelf: 'center',
                }}
              ></div>
              <div>
                <div
                  style={{
                    fontSize: '11px',
                    color: '#7c3aed',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontWeight: '600',
                  }}
                >
                  Juste Valeur
                </div>
                <div style={{ fontSize: '22px', fontWeight: '900', color: '#7c3aed' }}>
                  {formatPrice(synth.prix_pondere || valuationData.prix_pondere)}
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-around',
                borderTop: '1px solid var(--border-color)',
                paddingTop: '16px',
              }}
            >
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Potentiel</div>
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color:
                      (synth.potentiel_pct || valuationData.potentiel_pct) > 0
                        ? '#22c55e'
                        : (synth.potentiel_pct || valuationData.potentiel_pct) < 0
                          ? '#ef4444'
                          : 'var(--text-main)',
                  }}
                >
                  {(synth.potentiel_pct || valuationData.potentiel_pct) != null
                    ? `${(synth.potentiel_pct || valuationData.potentiel_pct) > 0 ? '+' : ''}${synth.potentiel_pct || valuationData.potentiel_pct}%`
                    : '—'}
                </div>
              </div>
              <div style={{ width: '1px', background: 'var(--border-color)' }}></div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Recomm.</div>
                <div
                  style={{
                    fontSize: '13px',
                    fontWeight: 'bold',
                    color: 'var(--text-heading)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getRecommendationEmoji(synth.recommendation || valuationData.recommendation)}{' '}
                  {synth.recommendation || valuationData.recommendation}
                </div>
              </div>
              <div style={{ width: '1px', background: 'var(--border-color)' }}></div>
              <div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Fiabilité</div>
                <div
                  style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: getReliabilityColor(synth.fiabilite || valuationData.fiabilite),
                  }}
                >
                  {synth.fiabilite || valuationData.fiabilite || 'N/A'}
                </div>
              </div>
            </div>
            {valuationDates.length > 0 && (
              <div
                style={{
                  marginTop: '14px',
                  fontSize: '11px',
                  color: 'var(--text-muted)',
                  lineHeight: 1.5,
                }}
              >
                {valuationDates.map(d => (
                  <div key={d}>🗓️ {d}</div>
                ))}
              </div>
            )}
          </div>

          {/* Range Visualizer Mobile */}
          {(() => {
            const prixMin = synth.prix_min || valuationData.prix_min;
            const prixMax = synth.prix_max || valuationData.prix_max;
            const cours = valuationData.cours_actuel;
            const prixPondere = synth.prix_pondere || valuationData.prix_pondere;

            if (!prixMin || !prixMax) return null;
            const range = prixMax - prixMin;
            const getPosition = (price: number) => {
              if (!price || range <= 0) return 0;
              return Math.max(0, Math.min(100, ((price - prixMin) / range) * 100));
            };

            const coursPos = getPosition(cours);
            const ponPos = getPosition(prixPondere);

            return (
              <div
                style={{
                  background: 'var(--bg-box)',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid var(--border-color)',
                  boxShadow: '0 1px 6px -2px var(--shadow-color)',
                }}
              >
                <h3
                  style={{
                    margin: '0 0 16px',
                    fontSize: '14px',
                    color: 'var(--text-heading)',
                    textAlign: 'center',
                  }}
                >
                  Fourchette de Valorisation
                </h3>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '8px',
                    fontSize: '11px',
                    color: 'var(--text-muted)',
                  }}
                >
                  <span>Min: {formatPrice(prixMin)}</span>
                  <span>Max: {formatPrice(prixMax)}</span>
                </div>

                <div
                  style={{
                    position: 'relative',
                    height: '20px',
                    background:
                      'linear-gradient(90deg, #ef4444 0%, #fbbf24 25%, #22c55e 50%, #fbbf24 75%, #ef4444 100%)',
                    borderRadius: '10px',
                    marginBottom: '30px',
                  }}
                >
                  {/* Pondere Marker */}
                  <div
                    style={{
                      position: 'absolute',
                      left: `${ponPos}%`,
                      top: '-6px',
                      transform: 'translateX(-50%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ width: '2px', height: '32px', background: '#7c3aed' }}></div>
                    <div
                      style={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: '#7c3aed',
                        marginTop: '2px',
                      }}
                    >
                      Pond.
                    </div>
                  </div>
                  {/* Cours Marker */}
                  {cours && (
                    <div
                      style={{
                        position: 'absolute',
                        left: `${coursPos}%`,
                        bottom: '-10px',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '10px',
                          fontWeight: 'bold',
                          color: 'var(--text-main)',
                          marginBottom: '2px',
                          background: 'var(--bg-box)',
                          padding: '2px 4px',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                        }}
                      >
                        Cours
                      </div>
                      <div
                        style={{ width: '2px', height: '16px', background: 'var(--text-main)' }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Models List */}
          <div
            style={{
              background: 'var(--bg-box)',
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid var(--border-color)',
              boxShadow: '0 1px 6px -2px var(--shadow-color)',
            }}
          >
            <button
              onClick={() => setMobileValuationExpanded(!mobileValuationExpanded)}
              style={{
                padding: '14px 16px',
                background: 'var(--bg-hover)',
                borderBottom: mobileValuationExpanded ? '1px solid var(--border-color)' : 'none',
                fontWeight: '600',
                fontSize: '14px',
                color: 'var(--text-heading)',
                width: '100%',
                textAlign: 'left',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <span>
                Détail des modèles ({synth.nb_modeles_valides || valuationData.nb_modeles_valides})
              </span>
              <span>{mobileValuationExpanded ? '▲' : '▼'}</span>
            </button>

            {mobileValuationExpanded && (
              <div>
                {renderMobileModelCard('Yield Reversion', '📊', valuationData.yield_reversion)}
                {renderMobileModelCard('Gordon-Shapiro', '📈', valuationData.gordon_shapiro)}
                {renderMobileModelCard('Chowder (1 an)', '🎯', valuationData.chowder_projection)}
                {renderMobileModelCard('CAPM Proj.', '⚖️', valuationData.capm_projection)}
                {renderMobileModelCard('Graham Simpl.', '📚', valuationData.graham_simplified)}

                {/* Multiples logic - try valuationData.market_multiples or the separate marketMultiplesData state */}
                {(() => {
                  const multiples = valuationData.market_multiples || marketMultiplesData;
                  if (!multiples) return null;
                  return (
                    <>
                      {renderMobileModelCard('P/E Model', '📈', multiples.pe_model)}
                      {renderMobileModelCard('EV/EBITDA', '📉', multiples.ev_ebitda_model)}
                      {renderMobileModelCard('EV/EBIT', '⚙️', multiples.ev_ebit_model)}
                      {renderMobileModelCard('P/S Model', '📊', multiples.ps_model)}
                      {renderMobileModelCard('EV/CA (Rev)', '💰', multiples.ev_revenue_model)}
                      {renderMobileModelCard('P/B Model', '📚', multiples.pb_model)}
                      {renderMobileModelCard('RIM (Ohlson)', '🏦', multiples.rim_model)}
                      {renderMobileModelCard('Graham Number', '🔢', multiples.graham_number)}
                      {renderMobileModelCard('EPV Method', '⚡', multiples.epv_model)}
                    </>
                  );
                })()}

                {valuationData.synthesis?.dcf_model &&
                  renderMobileModelCard('DCF Model', '🌀', valuationData.synthesis.dcf_model)}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: 'grid', gap: '24px' }}>
        {/* Summary Card */}
        <Box>
          <div
            style={{
              padding: '30px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              color: 'white',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '20px',
              }}
            >
              <div>
                <h2 style={{ margin: 0, fontSize: '24px' }}>
                  💎 Indice de Valeur Kanea - {selectedStock}
                </h2>
                {valuationDates.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
                    {valuationDates.map(d => (
                      <span
                        key={d}
                        style={{
                          background: 'rgba(255,255,255,0.2)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '13px',
                          fontWeight: 600,
                        }}
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '14px', opacity: 0.8 }}>Cours Actuel</div>
                <div style={{ fontSize: '28px', fontWeight: 'bold' }}>
                  {formatPrice(valuationData.cours_actuel)}
                </div>
              </div>
            </div>

            {/* Synthesis Bar - Min to Max Range */}
            {(() => {
              const synthesis = valuationData.synthesis || valuationData;
              const prixMin = synthesis.prix_min;
              const prixMax = synthesis.prix_max;
              const prixMoyen = synthesis.prix_moyen || valuationData.prix_moyen;
              const prixPondere = synthesis.prix_pondere || valuationData.prix_pondere;
              const methodology = synthesis.methodology || valuationData.methodology;
              const cours = valuationData.cours_actuel;

              if (!prixMin || !prixMax) return null;

              const range = prixMax - prixMin;
              const getPosition = (price: number) => {
                if (!price || range <= 0) return 0;
                return Math.max(0, Math.min(100, ((price - prixMin) / range) * 100));
              };

              const coursPosition = getPosition(cours);
              const moyennePosition = getPosition(prixMoyen);
              const ponderePosition = getPosition(prixPondere);

              return (
                <div style={{ marginTop: '30px' }}>
                  {/* Range Labels */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '8px',
                      fontSize: '13px',
                    }}
                  >
                    <span style={{ color: 'rgba(255,255,255,0.8)' }}>
                      📉 Min: {formatPrice(prixMin)}
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.8)' }}>
                      📈 Max: {formatPrice(prixMax)}
                    </span>
                  </div>

                  {/* Range Bar */}
                  <div
                    style={{
                      position: 'relative',
                      height: '40px',
                      background:
                        'linear-gradient(90deg, #ef4444 0%, #fbbf24 25%, #22c55e 50%, #fbbf24 75%, #ef4444 100%)',
                      borderRadius: '20px',
                      overflow: 'visible',
                    }}
                  >
                    {/* Current Price Marker */}
                    {cours && (
                      <div
                        style={{
                          position: 'absolute',
                          left: `${coursPosition}%`,
                          top: '-8px',
                          transform: 'translateX(-50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <div
                          style={{
                            background: 'var(--bg-box)',
                            color: 'var(--text-main)',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                            border: '1px solid var(--border-color)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          }}
                        >
                          📍 Cours: {formatPrice(cours)}
                        </div>
                        <div
                          style={{
                            width: '3px',
                            height: '40px',
                            background: 'var(--bg-box)',
                          }}
                        ></div>
                      </div>
                    )}

                    {/* Weighted Average Marker */}
                    {prixPondere && (
                      <div
                        style={{
                          position: 'absolute',
                          left: `${ponderePosition}%`,
                          bottom: '-30px',
                          transform: 'translateX(-50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <div
                          style={{
                            width: '2px',
                            height: '15px',
                            background: '#7c3aed',
                          }}
                        ></div>
                        <div
                          style={{
                            background: '#7c3aed',
                            color: '#fff',
                            padding: '3px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          ⚖️ Pondéré: {formatPrice(prixPondere)}
                        </div>
                      </div>
                    )}

                    {/* Simple Average Marker (if different) */}
                    {prixMoyen && Math.abs(moyennePosition - ponderePosition) > 5 && (
                      <div
                        style={{
                          position: 'absolute',
                          left: `${moyennePosition}%`,
                          bottom: '-30px',
                          transform: 'translateX(-50%)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <div
                          style={{
                            width: '2px',
                            height: '15px',
                            background: '#6b7280',
                          }}
                        ></div>
                        <div
                          style={{
                            background: '#6b7280',
                            color: '#fff',
                            padding: '3px 6px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Simple: {formatPrice(prixMoyen)}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Methodology Breakdown */}
                  {methodology && (
                    <div style={{ marginTop: '50px', fontSize: '12px', opacity: 0.9 }}>
                      <strong>Méthodologie :</strong> Pondération dynamique basée sur 15 modèles (dont RIM pour les banques) (
                      {valuationData.synthesis?.nb_modeles_valides ||
                        valuationData.nb_modeles_valides}{' '}
                      valides)
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Stats Cards */}
            <div
              style={{
                marginTop: valuationData.synthesis?.methodology ? '20px' : '30px',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px',
              }}
            >
              <div
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  padding: '20px',
                  borderRadius: '10px',
                }}
              >
                <div style={{ fontSize: '14px', opacity: 0.8 }}>💜 Prix Pondéré BRVM</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {formatPrice(valuationData.synthesis?.prix_pondere || valuationData.prix_pondere)}
                </div>
              </div>
              <div
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  padding: '20px',
                  borderRadius: '10px',
                }}
              >
                <div style={{ fontSize: '14px', opacity: 0.8 }}>Potentiel</div>
                <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                  {(() => {
                    const pot =
                      valuationData.synthesis?.potentiel_pct ?? valuationData.potentiel_pct;
                    return pot !== null ? `${pot > 0 ? '+' : ''}${pot}%` : '—';
                  })()}
                </div>
              </div>
              <div
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  padding: '20px',
                  borderRadius: '10px',
                }}
              >
                <div style={{ fontSize: '14px', opacity: 0.8 }}>Statut Valorisation</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  {getRecommendationEmoji(
                    valuationData.synthesis?.recommendation || valuationData.recommendation
                  )}{' '}
                  {valuationData.synthesis?.recommendation || valuationData.recommendation}
                </div>
              </div>
              <div
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  padding: '20px',
                  borderRadius: '10px',
                }}
              >
                <div style={{ fontSize: '14px', opacity: 0.8 }}>💎 Fiabilité</div>
                {/* Texte blanc + pastille de couleur : la couleur seule est illisible sur le dégradé */}
                <div
                  style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <span
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      border: '2px solid #ffffff',
                      background: getReliabilityColor(
                        valuationData.synthesis?.fiabilite || valuationData.fiabilite
                      ),
                    }}
                  />
                  {valuationData.synthesis?.fiabilite || valuationData.fiabilite || 'N/A'}
                </div>
              </div>
            </div>
          </div>
        </Box>

        {/* ── Classification Lynch (réservé admin — fonctionnalité en fiabilisation) ── */}
        {isAdmin && lynchClassification?.categorie && (
          <Box>
            <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
              <span style={{ fontSize: '26px' }}>{lynchClassification.categorie_emoji}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: '14px', color: 'var(--text-heading)' }}>
                    Profil Lynch : {lynchClassification.categorie_titre}
                  </strong>
                  <span
                    style={{
                      fontSize: '9px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      padding: '2px 6px',
                      borderRadius: '10px',
                      background: 'rgba(245, 158, 11, 0.15)',
                      color: '#b45309',
                    }}
                    title="Fonctionnalité en cours de fiabilisation, non exposée aux clients"
                  >
                    Admin only
                  </span>
                </div>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                  {lynchClassification.interpretation}
                </p>
                {lynchClassification.pending_categorie && (
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#b45309' }}>
                    Changement en cours de confirmation → {lynchClassification.pending_categorie}
                  </p>
                )}
              </div>
            </div>
          </Box>
        )}

        {/* ── Graphe Évolution Historique (réservé admin — fiabilisation en cours) ── */}
        {isAdmin && (() => {
          // Filtrer les données selon la période sélectionnée
          const now = new Date();
          const periodDays: Record<string, number> = {
            '1M': 30,
            '3M': 90,
            '6M': 180,
            MAX: Infinity,
          };
          const days = periodDays[valuationHistoryPeriod];
          const filtered = valuationHistory.filter((p) => {
            const d = new Date(p.date);
            return (now.getTime() - d.getTime()) / 86400000 <= days;
          });

          // Stats track record
          const totalDays = filtered.length;
          const undervaluedDays = filtered.filter(
            (p) => p.recommendation === 'SOUS-ÉVALUÉE'
          ).length;
          const overvaluedDays = filtered.filter((p) => p.recommendation === 'SUR-ÉVALUÉE').length;
          const fairValueDays = totalDays - undervaluedDays - overvaluedDays;

          // Couleur du dominante recommandation
          const dominantRec =
            undervaluedDays > overvaluedDays
              ? 'SOUS-ÉVALUÉE'
              : overvaluedDays > undervaluedDays
                ? 'SUR-ÉVALUÉE'
                : 'JUSTE VALEUR';
          const domColor =
            dominantRec === 'SOUS-ÉVALUÉE'
              ? '#10b981'
              : dominantRec === 'SUR-ÉVALUÉE'
                ? '#ef4444'
                : '#f59e0b';

          const formatTooltipDate = (val: string) => {
            if (!val) return '';
            const d = new Date(val);
            return d.toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: 'short',
              year: '2-digit',
            });
          };

          const CustomTooltip = ({ active, payload, label }: any) => {
            if (!active || !payload || !payload.length) return null;
            const pt: ValuationHistoryPoint = payload[0]?.payload;
            if (!pt) return null;
            const potentiel = pt.potentiel_pct;
            const potSign = potentiel > 0 ? '+' : '';
            const recColor =
              pt.recommendation === 'SOUS-ÉVALUÉE'
                ? '#10b981'
                : pt.recommendation === 'SUR-ÉVALUÉE'
                  ? '#ef4444'
                  : '#f59e0b';
            return (
              <div
                style={{
                  background: 'var(--bg-box)',
                  border: `1px solid ${recColor}`,
                  borderRadius: '10px',
                  padding: '12px 16px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                  minWidth: '190px',
                  fontSize: '13px',
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    color: 'var(--text-heading)',
                    marginBottom: '8px',
                    borderBottom: '1px solid var(--border-color)',
                    paddingBottom: '6px',
                  }}
                >
                  📅 {formatTooltipDate(pt.date)}
                </div>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}
                >
                  <span style={{ color: 'var(--text-muted)' }}>Cours réel</span>
                  <span style={{ fontWeight: 600, color: 'var(--text-main)' }}>
                    {pt.cours_actuel?.toLocaleString('fr-FR')} F
                  </span>
                </div>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}
                >
                  <span style={{ color: 'var(--text-muted)' }}>Prix Juste</span>
                  <span style={{ fontWeight: 600, color: '#a78bfa' }}>
                    {pt.prix_pondere?.toLocaleString('fr-FR')} F
                  </span>
                </div>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}
                >
                  <span style={{ color: 'var(--text-muted)' }}>Potentiel</span>
                  <span style={{ fontWeight: 700, color: potentiel > 0 ? '#10b981' : '#ef4444' }}>
                    {potSign}
                    {potentiel?.toFixed(1)}%
                  </span>
                </div>
                <div
                  style={{
                    display: 'inline-block',
                    background: recColor + '22',
                    color: recColor,
                    border: `1px solid ${recColor}`,
                    borderRadius: '20px',
                    padding: '2px 10px',
                    fontSize: '11px',
                    fontWeight: 700,
                  }}
                >
                  {pt.recommendation === 'SOUS-ÉVALUÉE'
                    ? '🚀'
                    : pt.recommendation === 'SUR-ÉVALUÉE'
                      ? '⚠️'
                      : '➡️'}{' '}
                  {pt.recommendation}
                </div>
              </div>
            );
          };

          return (
            <div
              style={{
                background: 'var(--bg-box)',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                overflow: 'hidden',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: '20px 24px 16px',
                  borderBottom: '1px solid var(--border-color)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px',
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: 700,
                      color: 'var(--text-heading)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    📈 Évolution Historique du Prix Juste
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                    Prix Juste Kanea (pondéré) vs Cours Réel · {totalDays} jours de données
                  </p>
                </div>
                {/* Période selector */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  {(['1M', '3M', '6M', 'MAX'] as const).map((p) => (
                    <button
                      key={p}
                      onClick={() => setValuationHistoryPeriod(p)}
                      style={{
                        padding: '5px 12px',
                        borderRadius: '20px',
                        border:
                          valuationHistoryPeriod === p
                            ? '1.5px solid #7c3aed'
                            : '1.5px solid var(--border-color)',
                        background:
                          valuationHistoryPeriod === p ? 'rgba(124,58,237,0.12)' : 'transparent',
                        color: valuationHistoryPeriod === p ? '#7c3aed' : 'var(--text-muted)',
                        fontWeight: valuationHistoryPeriod === p ? 700 : 400,
                        fontSize: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Track Record Badges */}
              {totalDays > 0 && (
                <div
                  style={{
                    padding: '12px 24px',
                    background: 'var(--bg-hover)',
                    borderBottom: '1px solid var(--border-color)',
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>
                    Track Record :
                  </span>
                  <span
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px' }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#10b981',
                      }}
                    ></span>
                    <span style={{ color: 'var(--text-main)' }}>
                      <strong>{undervaluedDays}j</strong> Sous-évalués
                    </span>
                  </span>
                  <span
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px' }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#f59e0b',
                      }}
                    ></span>
                    <span style={{ color: 'var(--text-main)' }}>
                      <strong>{fairValueDays}j</strong> Juste Valeur
                    </span>
                  </span>
                  <span
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px' }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#ef4444',
                      }}
                    ></span>
                    <span style={{ color: 'var(--text-main)' }}>
                      <strong>{overvaluedDays}j</strong> Sur-évalués
                    </span>
                  </span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      background: domColor + '1a',
                      color: domColor,
                      border: `1px solid ${domColor}40`,
                      borderRadius: '20px',
                      padding: '3px 12px',
                      fontSize: '11px',
                      fontWeight: 700,
                    }}
                  >
                    Signal dominant : {dominantRec}
                  </span>
                </div>
              )}

              {/* Chart */}
              <div style={{ padding: '16px 8px 8px' }}>
                {valuationHistoryLoading ? (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '240px',
                      color: 'var(--text-muted)',
                      fontSize: '14px',
                      gap: '10px',
                    }}
                  >
                    <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>
                      ⏳
                    </span>{' '}
                    Chargement de l'historique...
                  </div>
                ) : filtered.length === 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '200px',
                      color: 'var(--text-muted)',
                      textAlign: 'center',
                      gap: '12px',
                    }}
                  >
                    <span style={{ fontSize: '36px' }}>📭</span>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                        Pas encore de données historiques
                      </div>
                      <div style={{ fontSize: '12px' }}>
                        Le cache de valorisation est disponible depuis le 19 mars 2026.
                      </div>
                    </div>
                  </div>
                ) : (
                  <ResponsiveContainer width='100%' height={280}>
                    <ComposedChart
                      data={filtered}
                      margin={{ top: 10, right: 20, left: 10, bottom: 10 }}
                    >
                      <defs>
                        <linearGradient id='gradPrixJuste' x1='0' y1='0' x2='0' y2='1'>
                          <stop offset='5%' stopColor='#7c3aed' stopOpacity={0.25} />
                          <stop offset='95%' stopColor='#7c3aed' stopOpacity={0.02} />
                        </linearGradient>
                        <linearGradient id='gradCours' x1='0' y1='0' x2='0' y2='1'>
                          <stop offset='5%' stopColor='#06b6d4' stopOpacity={0.15} />
                          <stop offset='95%' stopColor='#06b6d4' stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray='3 3'
                        stroke='var(--border-color)'
                        vertical={false}
                      />
                      <XAxis
                        dataKey='date'
                        tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(val) => {
                          const d = new Date(val);
                          return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
                        }}
                        interval={Math.floor(filtered.length / 6)}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: 'var(--text-muted)' }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(val) => (val >= 1000 ? `${(val / 1000).toFixed(0)}k` : val)}
                        width={45}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      {/* Zone Prix Juste */}
                      <Area
                        type='monotone'
                        dataKey='prix_pondere'
                        name='Prix Juste'
                        stroke='#7c3aed'
                        strokeWidth={2.5}
                        fill='url(#gradPrixJuste)'
                        dot={false}
                        activeDot={{ r: 5, fill: '#7c3aed', stroke: '#fff', strokeWidth: 2 }}
                      />
                      {/* Ligne Cours Réel */}
                      <Area
                        type='monotone'
                        dataKey='cours_actuel'
                        name='Cours Réel'
                        stroke='#06b6d4'
                        strokeWidth={2}
                        fill='url(#gradCours)'
                        dot={false}
                        activeDot={{ r: 5, fill: '#06b6d4', stroke: '#fff', strokeWidth: 2 }}
                        strokeDasharray='5 3'
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Legend */}
              {filtered.length > 0 && (
                <div
                  style={{
                    padding: '10px 24px 16px',
                    display: 'flex',
                    gap: '20px',
                    flexWrap: 'wrap',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <div
                      style={{
                        width: '24px',
                        height: '3px',
                        background: '#7c3aed',
                        borderRadius: '2px',
                      }}
                    ></div>
                    Prix Juste Kanea (pondéré)
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '12px',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <div
                      style={{
                        width: '24px',
                        height: '2px',
                        background: '#06b6d4',
                        borderRadius: '2px',
                        borderTop: '2px dashed #06b6d4',
                      }}
                    ></div>
                    Cours Réel BRVM
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* 4 Models Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px',
          }}
        >
          {/* 1. Yield Reversion */}
          {(() => {
            const model = valuationData.yield_reversion;
            if (!model?.prix_juste) return null;
            const style = getPriceCardStyle(model?.prix_juste, valuationData.cours_actuel);
            return (
              <Box>
                <div
                  style={{
                    padding: '20px',
                    borderLeft: `4px solid ${style.borderColor}`,
                    background: style.bgColor,
                  }}
                >
                  <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                    📊 Yield Reversion
                  </h3>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}>
                    Prix = Dividende / Rendement Moyen Historique
                  </p>
                  <div
                    style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--text-heading)' }}
                  >
                    {formatPrice(model?.prix_juste)}
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}>
                    Rendement moyen: {model?.rendement_moyen ? `${model.rendement_moyen}%` : '—'}
                    <br />
                    Dernier dividende: {formatPrice(model?.dernier_dividende)}
                  </div>
                  <p
                    style={{
                      marginTop: '10px',
                      fontSize: '13px',
                      fontStyle: 'italic',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {model?.interpretation}
                  </p>
                </div>
              </Box>
            );
          })()}

          {/* 2. Gordon-Shapiro */}
          {(() => {
            const model = valuationData.gordon_shapiro;
            if (!model?.prix_juste) return null;
            const style = getPriceCardStyle(model?.prix_juste, valuationData.cours_actuel);
            return (
              <Box>
                <div
                  style={{
                    padding: '20px',
                    borderLeft: `4px solid ${style.borderColor}`,
                    background: style.bgColor,
                  }}
                >
                  <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                    📈 Gordon-Shapiro (DDM)
                  </h3>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}>
                    Prix = D₁ / (r - g) - Modèle de dividende actualisé
                  </p>
                  <div
                    style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--text-heading)' }}
                  >
                    {formatPrice(model?.prix_juste)}
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}>
                    Croissance dividende (g):{' '}
                    {model?.croissance_dividende ? `${model.croissance_dividende}%` : '—'}
                    <br />
                    Taux actualisation (r):{' '}
                    {model?.taux_actualisation ? `${model.taux_actualisation}%` : '—'}
                  </div>
                  <p
                    style={{
                      marginTop: '10px',
                      fontSize: '13px',
                      fontStyle: 'italic',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {model?.interpretation}
                  </p>
                </div>
              </Box>
            );
          })()}

          {/* 3. Chowder Projection */}
          {(() => {
            const model = valuationData.chowder_projection;
            if (!model?.prix_cible) return null;
            const style = getPriceCardStyle(model?.prix_cible, valuationData.cours_actuel);
            return (
              <Box>
                <div
                  style={{
                    padding: '20px',
                    borderLeft: `4px solid ${style.borderColor}`,
                    background: style.bgColor,
                  }}
                >
                  <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                    🎯 Projection Chowder (1 an)
                  </h3>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}>
                    Prix Cible = Cours × (1 + Yield + CAGR)
                  </p>
                  <div
                    style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--text-heading)' }}
                  >
                    {formatPrice(model?.prix_cible)}
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}>
                    Score Chowder: {model?.score_chowder ? `${model.score_chowder}%` : '—'}
                    <br />
                    Yield: {model?.yield_actuel ? `${model.yield_actuel}%` : '—'} + CAGR:{' '}
                    {model?.cagr_dividende ? `${model.cagr_dividende}%` : '—'}
                  </div>
                  <p
                    style={{
                      marginTop: '10px',
                      fontSize: '13px',
                      fontStyle: 'italic',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {model?.interpretation}
                  </p>
                </div>
              </Box>
            );
          })()}

          {/* 4. CAPM Projection */}
          {(() => {
            const model = valuationData.capm_projection;
            if (!model?.prix_cible) return null;
            const style = getPriceCardStyle(model?.prix_cible, valuationData.cours_actuel);
            return (
              <Box>
                <div
                  style={{
                    padding: '20px',
                    borderLeft: `4px solid ${style.borderColor}`,
                    background: style.bgColor,
                  }}
                >
                  <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                    ⚖️ Projection CAPM
                  </h3>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}>
                    Prix Cible = Cours × (1 + Rf + β×(Rm-Rf))
                  </p>
                  <div
                    style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--text-heading)' }}
                  >
                    {formatPrice(model?.prix_cible)}
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}>
                    Rendement exigé: {model?.rendement_exige ? `${model.rendement_exige}%` : '—'}
                    <br />
                    Beta (β): {model?.beta ?? '—'}
                  </div>
                  <p
                    style={{
                      marginTop: '10px',
                      fontSize: '13px',
                      fontStyle: 'italic',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {model?.interpretation}
                  </p>
                </div>
              </Box>
            );
          })()}

          {/* 5. Graham Simplifié */}
          {(() => {
            const model = valuationData.graham_simplified;
            if (!model?.prix_juste) return null;
            const style = getPriceCardStyle(model?.prix_juste, valuationData.cours_actuel);
            return (
              <Box>
                <div
                  style={{
                    padding: '20px',
                    borderLeft: `4px solid ${style.borderColor}`,
                    background: style.bgColor,
                  }}
                >
                  <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                    📚 Graham Simplifié
                  </h3>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}>
                    V = Dividende / (Taux Obligations × 0.67)
                  </p>
                  <div
                    style={{ fontSize: '28px', fontWeight: 'bold', color: 'var(--text-heading)' }}
                  >
                    {formatPrice(model?.prix_juste)}
                  </div>
                  <div style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}>
                    Taux obligataire: {model?.taux_obligataire ? `${model.taux_obligataire}%` : '—'}
                    <br />
                    Taux Graham (×0.67): {model?.taux_graham ? `${model.taux_graham}%` : '—'}
                  </div>
                  <p
                    style={{
                      marginTop: '10px',
                      fontSize: '13px',
                      fontStyle: 'italic',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {model?.interpretation}
                  </p>
                </div>
              </Box>
            );
          })()}
        </div>

        {/* Market Multiples Section (P/E, EV/EBITDA, P/S) */}
        {marketMultiplesData && marketMultiplesData.has_data && (
          <>
            <div
              style={{
                marginTop: '30px',
                marginBottom: '20px',
                padding: '16px 20px',
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                borderRadius: '10px',
                color: 'white',
              }}
            >
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                📊 Modèles de Multiples et Intrinsèques
              </h3>
              <p style={{ margin: '8px 0 0', fontSize: '14px', opacity: 0.9 }}>
                Multiples (P/E, EV/EBITDA, EV/EBIT, P/S, EV/CA, P/B) : appliquent aux fondamentaux de
                l'entreprise un multiple de référence, historique ou sectoriel. Intrinsèques (RIM,
                Graham, EPV, DCF) : estiment la valeur à partir des seuls fondamentaux (fonds propres,
                bénéfices, flux de trésorerie).
              </p>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px',
              }}
            >
              {/* 7. P/E Model */}
              {(() => {
                const model = marketMultiplesData.pe_model;
                if (!model?.prix_juste) return null;
                const style = getPriceCardStyle(model?.prix_juste, valuationData?.cours_actuel);
                return (
                  <Box>
                    <div
                      style={{
                        padding: '20px',
                        borderLeft: `4px solid ${style.borderColor}`,
                        background: style.bgColor,
                      }}
                    >
                      <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                        💹 P/E (Price-to-Earnings)
                      </h3>
                      <p
                        style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}
                      >
                        Prix = BPA × Multiple P/E historique
                      </p>
                      <div
                        style={{
                          fontSize: '28px',
                          fontWeight: 'bold',
                          color: 'var(--text-heading)',
                        }}
                      >
                        {formatPrice(model?.prix_juste)}
                      </div>
                      <div
                        style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}
                      >
                        BPA (Bénéfice par action):{' '}
                        {model?.bpa ? `${model.bpa.toLocaleString('fr-FR')} FCFA` : '—'}
                        <br />
                        Multiple P/E moyen: {model?.multiple_pe ? `${model.multiple_pe}x` : '—'}
                        <br />
                        P/E actuel: {model?.pe_actuel ? `${model.pe_actuel}x` : '—'}
                      </div>
                      <p
                        style={{
                          marginTop: '10px',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {model?.interpretation}
                      </p>
                      {model?.closing_date && (
                        <p
                          style={{ marginTop: '5px', fontSize: '11px', color: 'var(--text-muted)' }}
                        >
                          Données: {model.closing_date}
                        </p>
                      )}
                    </div>
                  </Box>
                );
              })()}

              {/* 8. EV/EBITDA Model */}
              {(() => {
                const model = marketMultiplesData.ev_ebitda_model;
                if (!model?.prix_juste) return null;
                const style = getPriceCardStyle(model?.prix_juste, valuationData?.cours_actuel);
                const formatCompact = (n: number | null | undefined) => {
                  if (!n) return '—';
                  const abs = Math.abs(n);
                  if (abs >= 1e9) return (n / 1e9).toFixed(1) + ' Mds';
                  if (abs >= 1e6) return (n / 1e6).toFixed(1) + ' M';
                  if (abs >= 1e3) return (n / 1e3).toFixed(1) + ' K';
                  return n.toLocaleString('fr-FR');
                };
                return (
                  <Box>
                    <div
                      style={{
                        padding: '20px',
                        borderLeft: `4px solid ${style.borderColor}`,
                        background: style.bgColor,
                      }}
                    >
                      <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                        🏢 EV/EBITDA
                      </h3>
                      <p
                        style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}
                      >
                        Prix = [(EBITDA × Multiple) - Dette Nette] / Actions
                      </p>
                      <div
                        style={{
                          fontSize: '28px',
                          fontWeight: 'bold',
                          color: 'var(--text-heading)',
                        }}
                      >
                        {formatPrice(model?.prix_juste)}
                      </div>
                      <div
                        style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}
                      >
                        EBITDA: {formatCompact(model?.ebitda)} FCFA
                        <br />
                        Dette Nette: {formatCompact(model?.net_debt)} FCFA
                        <br />
                        Multiple EV/EBITDA moyen:{' '}
                        {model?.multiple_ev_ebitda ? `${model.multiple_ev_ebitda}x` : '—'}
                      </div>
                      <p
                        style={{
                          marginTop: '10px',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {model?.interpretation}
                      </p>
                    </div>
                  </Box>
                );
              })()}

              {/* 8b. EV/EBIT Model */}
              {(() => {
                const model = marketMultiplesData.ev_ebit_model;
                if (!model?.prix_juste && !model?.ebit) return null;
                const style = getPriceCardStyle(model?.prix_juste, valuationData?.cours_actuel);
                const formatCompact = (n: number | null | undefined) => {
                  if (!n) return '—';
                  const abs = Math.abs(n);
                  if (abs >= 1e9) return (n / 1e9).toFixed(1) + ' Mds';
                  if (abs >= 1e6) return (n / 1e6).toFixed(1) + ' M';
                  return n.toLocaleString('fr-FR');
                };
                return (
                  <Box>
                    <div
                      style={{
                        padding: '20px',
                        borderLeft: `4px solid ${style.borderColor}`,
                        background: style.bgColor,
                      }}
                    >
                      <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                        ⚙️ EV/EBIT
                      </h3>
                      <p
                        style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}
                      >
                        Prix = [(EBIT × Multiple) - Dette Nette] / Actions
                      </p>
                      <div
                        style={{
                          fontSize: '28px',
                          fontWeight: 'bold',
                          color: 'var(--text-heading)',
                        }}
                      >
                        {formatPrice(model?.prix_juste)}
                      </div>
                      <div
                        style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}
                      >
                        EBIT (Exploitation): {formatCompact(model?.ebit)} FCFA
                        <br />
                        Dette Nette: {formatCompact(model?.net_debt)} FCFA
                        <br />
                        Multiple EBIT: {model?.multiple ? `${model.multiple}x` : '11.0x'}
                      </div>
                      <p
                        style={{
                          marginTop: '10px',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {model?.interpretation}
                      </p>
                    </div>
                  </Box>
                );
              })()}

              {/* 9. P/S Model */}
              {(() => {
                const model = marketMultiplesData.ps_model;
                if (!model?.prix_juste) return null;
                const style = getPriceCardStyle(model?.prix_juste, valuationData?.cours_actuel);
                const formatCompact = (n: number | null | undefined) => {
                  if (!n) return '—';
                  const abs = Math.abs(n);
                  if (abs >= 1e9) return (n / 1e9).toFixed(1) + ' Mds';
                  if (abs >= 1e6) return (n / 1e6).toFixed(1) + ' M';
                  if (abs >= 1e3) return (n / 1e3).toFixed(1) + ' K';
                  return n.toLocaleString('fr-FR');
                };
                return (
                  <Box>
                    <div
                      style={{
                        padding: '20px',
                        borderLeft: `4px solid ${style.borderColor}`,
                        background: style.bgColor,
                      }}
                    >
                      <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                        📈 P/S (Price-to-Sales)
                      </h3>
                      <p
                        style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}
                      >
                        Prix = (CA / Actions) × Multiple P/S moyen
                      </p>
                      <div
                        style={{
                          fontSize: '28px',
                          fontWeight: 'bold',
                          color: 'var(--text-heading)',
                        }}
                      >
                        {formatPrice(model?.prix_juste)}
                      </div>
                      <div
                        style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}
                      >
                        Chiffre d'Affaires: {formatCompact(model?.revenue)} FCFA
                        <br />
                        CA par action:{' '}
                        {model?.ca_par_action
                          ? `${model.ca_par_action.toLocaleString('fr-FR')} FCFA`
                          : '—'}
                        <br />
                        Multiple P/S moyen: {model?.multiple_ps ? `${model.multiple_ps}x` : '—'}
                      </div>
                      <p
                        style={{
                          marginTop: '10px',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {model?.interpretation}
                      </p>
                    </div>
                  </Box>
                );
              })()}

              {/* 9b. EV/Revenue Model */}
              {(() => {
                const model = marketMultiplesData.ev_revenue_model;
                if (!model?.prix_juste && !model?.revenue) return null;
                const style = getPriceCardStyle(model?.prix_juste, valuationData?.cours_actuel);
                const formatCompact = (n: number | null | undefined) => {
                  if (!n) return '—';
                  const abs = Math.abs(n);
                  if (abs >= 1e9) return (n / 1e9).toFixed(1) + ' Mds';
                  if (abs >= 1e6) return (n / 1e6).toFixed(1) + ' M';
                  return n.toLocaleString('fr-FR');
                };
                return (
                  <Box>
                    <div
                      style={{
                        padding: '20px',
                        borderLeft: `4px solid ${style.borderColor}`,
                        background: style.bgColor,
                      }}
                    >
                      <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                        💰 EV/CA (Revenue)
                      </h3>
                      <p
                        style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}
                      >
                        Prix = [(CA × Multiple) - Dette Nette] / Actions
                      </p>
                      <div
                        style={{
                          fontSize: '28px',
                          fontWeight: 'bold',
                          color: 'var(--text-heading)',
                        }}
                      >
                        {formatPrice(model?.prix_juste)}
                      </div>
                      <div
                        style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}
                      >
                        Chiffre d'Affaires: {formatCompact(model?.revenue)} FCFA
                        <br />
                        Dette Nette: {formatCompact(model?.net_debt)} FCFA
                        <br />
                        Multiple CA: {model?.multiple ? `${model.multiple}x` : '—'}
                      </div>
                      <p
                        style={{
                          marginTop: '10px',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {model?.interpretation}
                      </p>
                    </div>
                  </Box>
                );
              })()}
              {/* 10. P/B Model */}
              {(() => {
                const model = marketMultiplesData.pb_model;
                if (!model?.prix_juste) return null;
                const style = getPriceCardStyle(model?.prix_juste, valuationData?.cours_actuel);
                return (
                  <Box>
                    <div
                      style={{
                        padding: '20px',
                        borderLeft: `4px solid ${style.borderColor}`,
                        background: style.bgColor,
                      }}
                    >
                      <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                        📚 P/B (Price-to-Book)
                      </h3>
                      <p
                        style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}
                      >
                        Prix = VCN × Multiple P/B
                      </p>
                      <div
                        style={{
                          fontSize: '28px',
                          fontWeight: 'bold',
                          color: 'var(--text-heading)',
                        }}
                      >
                        {formatPrice(model?.prix_juste)}
                      </div>
                      <div
                        style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}
                      >
                        Valeur Comptable par Action:{' '}
                        {model?.vcn ? `${model.vcn.toLocaleString('fr-FR')} FCFA` : '—'}
                        <br />
                        Multiple P/B moyen: {model?.multiple_pb ? `${model.multiple_pb}x` : '—'}
                        <br />
                        P/B actuel: {model?.pb_actuel ? `${model.pb_actuel}x` : '—'}
                      </div>
                      <p
                        style={{
                          marginTop: '10px',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {model?.interpretation}
                      </p>
                    </div>
                  </Box>
                );
              })()}

              {/* 10b. RIM Model (Residual Income Model / Ohlson) */}
              {(() => {
                const model = marketMultiplesData.rim_model;
                if (!model?.prix_juste) return null;
                const style = getPriceCardStyle(model?.prix_juste, valuationData?.cours_actuel);
                return (
                  <Box>
                    <div
                      style={{
                        padding: '20px',
                        borderLeft: `4px solid ${style.borderColor}`,
                        background: style.bgColor,
                      }}
                    >
                      <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                        🏦 RIM (Modèle d'Ohlson)
                      </h3>
                      <p
                        style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}
                      >
                        {model?.fade_years
                          ? `Prix = BVPS + Σ (ROE_t - Ke) × BV_t-1 / (1 + Ke)^t, ROE → Ke en ${model.fade_years} ans`
                          : 'Prix = BVPS + [(ROE - Ke) / (Ke - g)] × BVPS'}
                      </p>
                      <div
                        style={{
                          fontSize: '28px',
                          fontWeight: 'bold',
                          color: 'var(--text-heading)',
                        }}
                      >
                        {formatPrice(model?.prix_juste)}
                      </div>
                      <div
                        style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}
                      >
                        Actif Net par Action (BVPS):{' '}
                        {model?.bvps ? `${model.bvps.toLocaleString('fr-FR')} FCFA` : '—'}
                        <br />
                        Rentabilité Fonds Propres (ROE):{' '}
                        {model?.roe ? `${model.roe}%` : '—'}
                        <br />
                        Coût des capitaux propres (Ke):{' '}
                        {model?.cost_of_equity ? `${(model.cost_of_equity * 100).toFixed(1)}%` : '—'}
                        <br />
                        Croissance pérenne (g):{' '}
                        {model?.growth_rate ? `${(model.growth_rate * 100).toFixed(0)}%` : '—'}
                      </div>
                      <p
                        style={{
                          marginTop: '10px',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {model?.interpretation}
                      </p>
                    </div>
                  </Box>
                );
              })()}

              {/* 11. Graham Number */}
              {(() => {
                const model = marketMultiplesData.graham_number;
                if (!model?.prix_juste) return null;
                const style = getPriceCardStyle(model?.prix_juste, valuationData?.cours_actuel);
                return (
                  <Box>
                    <div
                      style={{
                        padding: '20px',
                        borderLeft: `4px solid ${style.borderColor}`,
                        background: style.bgColor,
                      }}
                    >
                      <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                        🔢 Graham Number
                      </h3>
                      <p
                        style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}
                      >
                        Prix = √(22.5 × BPA × VCN)
                      </p>
                      <div
                        style={{
                          fontSize: '28px',
                          fontWeight: 'bold',
                          color: 'var(--text-heading)',
                        }}
                      >
                        {formatPrice(model?.prix_juste)}
                      </div>
                      <div
                        style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}
                      >
                        BPA (Bénéfice Par Action):{' '}
                        {model?.bpa ? `${model.bpa.toLocaleString('fr-FR')} FCFA` : '—'}
                        <br />
                        VCN (Valeur Comptable Nette):{' '}
                        {model?.vcn ? `${model.vcn.toLocaleString('fr-FR')} FCFA` : '—'}
                      </div>
                      <p
                        style={{
                          marginTop: '10px',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {model?.interpretation}
                      </p>
                    </div>
                  </Box>
                );
              })()}

              {/* 12. EPV Model */}
              {(() => {
                const model = marketMultiplesData.epv_model;
                if (!model?.prix_juste) return null;
                const style = getPriceCardStyle(model?.prix_juste, valuationData?.cours_actuel);
                const formatCompact = (n: number | null | undefined) => {
                  if (!n) return '—';
                  const abs = Math.abs(n);
                  if (abs >= 1e9) return (n / 1e9).toFixed(1) + ' Mds';
                  if (abs >= 1e6) return (n / 1e6).toFixed(1) + ' M';
                  return n.toLocaleString('fr-FR');
                };
                return (
                  <Box>
                    <div
                      style={{
                        padding: '20px',
                        borderLeft: `4px solid ${style.borderColor}`,
                        background: style.bgColor,
                      }}
                    >
                      <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                        ⚡ EPV (Earnings Power Value)
                      </h3>
                      <p
                        style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}
                      >
                        Prix = (Net Income / CoC) / Actions
                      </p>
                      <div
                        style={{
                          fontSize: '28px',
                          fontWeight: 'bold',
                          color: 'var(--text-heading)',
                        }}
                      >
                        {formatPrice(model?.prix_juste)}
                      </div>
                      <div
                        style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}
                      >
                        Résultat Net: {formatCompact(model?.net_income)} FCFA
                        <br />
                        Coût du Capital: {model?.cout_capital ? `${model.cout_capital}%` : '—'}
                      </div>
                      <p
                        style={{
                          marginTop: '10px',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          color: 'var(--text-muted)',
                        }}
                      >
                        {model?.interpretation}
                      </p>
                    </div>
                  </Box>
                );
              })()}

              {/* 13. DCF Model (Levered FCFE) */}
              {(() => {
                const model =
                  valuationData.synthesis?.dcf_model || (valuationData as any).dcf_model;
                if (!model?.prix_juste) return null;
                const style = getPriceCardStyle(model?.prix_juste, valuationData?.cours_actuel);
                const formatCompact = (n: number | null | undefined) => {
                  if (!n) return '—';
                  const abs = Math.abs(n);
                  if (abs >= 1e9) return (n / 1e9).toFixed(1) + ' Mds';
                  if (abs >= 1e6) return (n / 1e6).toFixed(1) + ' M';
                  return n.toLocaleString('fr-FR');
                };
                return (
                  <Box>
                    <div
                      style={{
                        padding: '20px',
                        borderLeft: `4px solid ${style.borderColor}`,
                        background: style.bgColor,
                      }}
                    >
                      <h3 style={{ margin: '0 0 10px', color: 'var(--text-heading)' }}>
                        🌀 DCF (Discounted Cash Flow)
                      </h3>
                      <p
                        style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 15px' }}
                      >
                        Prix = PV(FCFE 5 ans) + PV(Valeur Terminale)
                      </p>
                      <div
                        style={{
                          fontSize: '28px',
                          fontWeight: 'bold',
                          color: 'var(--text-heading)',
                        }}
                      >
                        {formatPrice(model?.prix_juste)}
                      </div>
                      <div
                        style={{ marginTop: '10px', fontSize: '14px', color: 'var(--text-muted)' }}
                      >
                        Net Income (TTM): {formatCompact(model?.net_income)} FCFA
                        <br />
                        Taux actualisation (Ke):{' '}
                        {model?.discount_rate_r ? `${model.discount_rate_r}%` : '—'}
                        <br />
                        Croissance (g): {model?.growth_rate_5y ? `${model.growth_rate_5y}%` : '—'}
                      </div>
                      <p
                        style={{
                          marginTop: '10px',
                          fontSize: '13px',
                          fontStyle: 'italic',
                          color: 'var(--text-muted)',
                        }}
                      >
                        Modèle de flux de trésorerie actualisés (FCFE) sur 5 ans.
                      </p>
                    </div>
                  </Box>
                );
              })()}
            </div>
          </>
        )}

        {/* Info when no fundamental data */}
        {marketMultiplesData && !marketMultiplesData.has_data && (
          <div
            style={{
              marginTop: '30px',
              padding: '20px',
              background: 'rgba(245, 158, 11, 0.1)',
              borderRadius: '10px',
              border: '1px solid rgba(245, 158, 11, 0.2)',
            }}
          >
            <h4 style={{ margin: '0 0 8px', color: '#f59e0b' }}>
              ℹ️ Modèles de Multiples et Intrinsèques non disponibles
            </h4>
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>
              Ces modèles nécessitent des données fondamentales (Chiffre d'Affaires, EBITDA,
              Résultat Net, Fonds propres). Ces données n'ont pas encore été saisies pour cette
              société.
            </p>
          </div>
        )}

        <div
          style={{
            marginTop: '15px',
            padding: '15px',
            background: 'var(--bg-box)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            fontSize: '13px',
            lineHeight: '1.5',
          }}
        >
          ℹ️ <strong>Avertissement Légal :</strong> L'Indice de Valeur Kanea est une estimation
          théorique basée sur{' '}
          {valuationData.synthesis?.nb_modeles_valides || valuationData.nb_modeles_valides || 14}{' '}
          modèles financiers (Gordon-Shapiro, Graham, etc.) pondérés selon la méthodologie
          propriétaire de Kanea. Fourni à titre purement indicatif et pédagogique. Cet outil ne
          constitue pas un conseil en investissement, ni une incitation à l'achat ou à la vente.
          L'investisseur est seul responsable de ses décisions.
        </div>
      </div>
    );
  };

  return (
    <SiteLayout>
      <Header icon='analytics' title='Terminal' />

      {isMobile ? (
        <MobileTerminal
          // Selection
          stocks={stocks}
          selectedStock={selectedStock}
          setSelectedStock={setSelectedStock}
          stockDetails={stockDetails}
          isFavorited={isFavorited}
          toggleFavorite={toggleFavorite}
          // Tabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          // Overview
          quotations={quotations}
          capitalisation={capitalisation}
          chartPeriod={chartPeriod}
          setChartPeriod={setChartPeriod}
          calculatePerformance={calculatePerformance}
          getYTDPerformance={getYTDPerformance}
          getWeeklyBreakdown={getWeeklyBreakdown}
          formatLargeNumber={formatLargeNumber}
          // Analysis
          loading={loading}
          chartData={chartData}
          renderAnalysis={renderAnalysis}
          // Dividends
          renderDividends={renderDividends}
          // Valuation
          valuationData={valuationData}
          renderValuation={renderValuation}
          renderPeers={renderPeers}
          // AI
          aiNarrative={aiNarrative}
          aiNarrativeDates={aiNarrativeDates}
          aiLoading={aiLoading}
          aiSummaryType={aiSummaryType}
          setAiSummaryType={setAiSummaryType}
          // Permissions
          hasPermission={hasPermission}
        />
      ) : (
        <div
          className='ds-container'
          style={{ padding: '20px', maxWidth: '1600px', margin: '0 auto' }}
        >
          {/* Stock Selector & Actions */}
          <div
            className='ds-stock-selector'
            style={{
              marginBottom: '20px',
              position: 'relative',
              zIndex: 100,
              display: 'flex',
              gap: '15px',
              alignItems: 'center',
            }}
          >
            <div style={{ flex: 1 }}>
              <StockSearch
                stocks={stocks}
                value={selectedStock || ''}
                onChange={(symbol) => setSelectedStock(symbol)}
                placeholder='Rechercher une société...'
              />
            </div>

            <button
              onClick={toggleFavorite}
              title={isFavorited ? 'Retirer des favoris' : 'Ajouter aux favoris'}
              style={{
                padding: '10px 16px',
                background: isFavorited ? '#fbbf24' : 'var(--bg-input)',
                border: isFavorited ? 'none' : '1px solid var(--border-color)',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: isFavorited ? 'white' : 'var(--text-main)',
                fontWeight: 'bold',
                height: '42px', // Match search bar height roughly
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ fontSize: '18px' }}>{isFavorited ? '★' : '☆'}</span>
              <span style={{ display: 'none' }} className='desktop-text'>
                {isFavorited ? 'Favori' : 'Suivre'}
              </span>
              {/* Simple media query hack for text visibility */}
              <style>{`
              @media (min-width: 768px) {
                .desktop-text { display: inline !important; }
              }
            `}</style>
            </button>
          </div>

          {/* Tab Navigation */}
          <div
            className='ds-tabs-wrapper'
            style={{
              marginBottom: '30px',
              borderBottom: '2px solid var(--border-color)',
              overflowX: 'auto',
            }}
          >
            <div
              className='ds-tabs'
              style={{ display: 'flex', gap: '8px', minWidth: 'max-content' }}
            >
              <button
                onClick={() => setActiveTab('overview')}
                style={{
                  padding: '10px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom:
                    activeTab === 'overview' ? '3px solid #667eea' : '3px solid transparent',
                  color: activeTab === 'overview' ? '#667eea' : 'var(--text-muted)',
                  fontWeight: activeTab === 'overview' ? 'bold' : 'normal',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                }}
              >
                📊 Vue d'ensemble
              </button>
              <button
                onClick={() => setActiveTab('analysis')}
                style={{
                  padding: '10px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom:
                    activeTab === 'analysis' ? '3px solid #667eea' : '3px solid transparent',
                  color: activeTab === 'analysis' ? '#667eea' : 'var(--text-muted)',
                  fontWeight: activeTab === 'analysis' ? 'bold' : 'normal',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                }}
              >
                📈 Analyse Graphique
              </button>
              <button
                onClick={() => setActiveTab('dividends')}
                style={{
                  padding: '10px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom:
                    activeTab === 'dividends' ? '3px solid #667eea' : '3px solid transparent',
                  color: activeTab === 'dividends' ? '#667eea' : 'var(--text-muted)',
                  fontWeight: activeTab === 'dividends' ? 'bold' : 'normal',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                }}
              >
                💰 Dividendes
              </button>
              <button
                onClick={() => {
                  if (!hasPermissionForStock('view_advanced_data')) {
                    navigate('/upgrade');
                    return;
                  }
                  setActiveTab('valuation');
                }}
                style={{
                  padding: '10px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom:
                    activeTab === 'valuation' ? '3px solid #667eea' : '3px solid transparent',
                  color: activeTab === 'valuation' ? '#667eea' : 'var(--text-muted)',
                  fontWeight: activeTab === 'valuation' ? 'bold' : 'normal',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  opacity: !hasPermissionForStock('view_advanced_data') ? 0.6 : 1,
                }}
              >
                💎 Indice Valeur {!hasPermissionForStock('view_advanced_data') && '🔒'}
              </button>

              <button
                onClick={() => {
                  if (!hasPermissionForStock('view_advanced_data')) {
                    navigate('/upgrade');
                    return;
                  }
                  setActiveTab('peers');
                }}
                style={{
                  padding: '10px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom:
                    activeTab === 'peers' ? '3px solid #10b981' : '3px solid transparent',
                  color: activeTab === 'peers' ? '#10b981' : 'var(--text-muted)',
                  fontWeight: activeTab === 'peers' ? 'bold' : 'normal',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  opacity: !hasPermissionForStock('view_advanced_data') ? 0.6 : 1,
                }}
              >
                👥 Comparaison (Pairs) {!hasPermissionForStock('view_advanced_data') && '🔒'}
              </button>

              <button
                onClick={() => {
                  if (!hasPermissionForStock('view_advanced_ai_data')) {
                    navigate('/upgrade');
                    return;
                  }
                  setActiveTab('ai_summary');
                }}
                style={{
                  padding: '10px 16px',
                  background: 'none',
                  border: 'none',
                  borderBottom:
                    activeTab === 'ai_summary' ? '3px solid #db2777' : '3px solid transparent',
                  color: activeTab === 'ai_summary' ? '#db2777' : '#666',
                  fontWeight: activeTab === 'ai_summary' ? 'bold' : 'normal',
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  opacity: !hasPermissionForStock('view_advanced_ai_data') ? 0.6 : 1,
                }}
              >
                🤖 L'avis de Nia {!hasPermissionForStock('view_advanced_ai_data') && '🔒'}
              </button>
            </div>
          </div>

          {/* Content Area */}
          {!selectedStock ? (
            <div className='ds-welcome-landing'>
              {/* Hero Section */}
              <div
                style={{
                  textAlign: 'center',
                  padding: '50px 30px 30px',
                  background:
                    'linear-gradient(135deg, rgba(102, 126, 234, 0.08), rgba(118, 75, 162, 0.08))',
                  borderRadius: '16px',
                  border: '1px solid rgba(102, 126, 234, 0.15)',
                  marginBottom: '30px',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Decorative floating dots */}
                <div
                  style={{
                    position: 'absolute',
                    top: '15px',
                    left: '20px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'rgba(102, 126, 234, 0.3)',
                    animation: 'pulse 3s ease-in-out infinite',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    top: '40px',
                    right: '40px',
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    background: 'rgba(118, 75, 162, 0.3)',
                    animation: 'pulse 3s ease-in-out infinite 1s',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '35%',
                    width: '5px',
                    height: '5px',
                    borderRadius: '50%',
                    background: 'rgba(102, 126, 234, 0.2)',
                    animation: 'pulse 3s ease-in-out infinite 0.5s',
                  }}
                />

                <div style={{ fontSize: '42px', marginBottom: '12px' }}>📊</div>
                <h2
                  style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    color: 'var(--text-main)',
                    marginBottom: '8px',
                    letterSpacing: '-0.3px',
                  }}
                >
                  Bienvenue sur le Terminal
                </h2>
                <p
                  style={{
                    fontSize: '15px',
                    color: 'var(--text-muted)',
                    maxWidth: '500px',
                    margin: '0 auto',
                    lineHeight: '1.5',
                  }}
                >
                  Sélectionnez une société ci-dessous ou utilisez la barre de recherche pour
                  démarrer votre analyse.
                </p>
              </div>

              {/* Stock Cards Grid grouped by sector */}
              {(() => {
                const sectorMap = new Map<string, Stock[]>();
                stocks.forEach((s) => {
                  // Only use the main sector (before '/') for grouping
                  const rawSector = s.secteur || 'Autres';
                  const sector = rawSector.split('/')[0].trim();
                  if (!sectorMap.has(sector)) sectorMap.set(sector, []);
                  sectorMap.get(sector)!.push(s);
                });

                const sectorIcons: Record<string, string> = {
                  INDUSTRIE: '🏭',
                  'SERVICES FINANCIERS': '🏦',
                  AGRICULTURE: '🌿',
                  DISTRIBUTION: '🛒',
                  'SERVICES PUBLICS': '⚡',
                  TRANSPORT: '🚢',
                  FINANCES: '🏦',
                  AUTRES: '📦',
                };
                const getSectorIcon = (s: string) => sectorIcons[s.toUpperCase()] || '📋';

                const sectorColors: Record<string, string> = {
                  INDUSTRIE: '#667eea',
                  'SERVICES FINANCIERS': '#10b981',
                  AGRICULTURE: '#f59e0b',
                  DISTRIBUTION: '#ef4444',
                  'SERVICES PUBLICS': '#8b5cf6',
                  TRANSPORT: '#06b6d4',
                  FINANCES: '#10b981',
                  AUTRES: '#6b7280',
                };
                const getSectorColor = (s: string) => sectorColors[s.toUpperCase()] || '#667eea';

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {Array.from(sectorMap.entries()).map(([sector, sectorStocks]) => (
                      <div key={sector}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '12px',
                            paddingBottom: '8px',
                            borderBottom: `2px solid ${getSectorColor(sector)}20`,
                          }}
                        >
                          <span style={{ fontSize: '18px' }}>{getSectorIcon(sector)}</span>
                          <h3
                            style={{
                              fontSize: '14px',
                              fontWeight: '700',
                              color: getSectorColor(sector),
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              margin: 0,
                            }}
                          >
                            {sector}
                          </h3>
                          <span
                            style={{
                              fontSize: '11px',
                              color: 'var(--text-muted)',
                              background: 'var(--bg-input)',
                              padding: '2px 8px',
                              borderRadius: '10px',
                              fontWeight: '600',
                            }}
                          >
                            {sectorStocks.length}
                          </span>
                        </div>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                            gap: '10px',
                          }}
                        >
                          {sectorStocks.map((stock) => (
                            <button
                              key={stock.symbole}
                              onClick={() => setSelectedStock(stock.symbole)}
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px 14px',
                                background: 'var(--bg-box)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                textAlign: 'left',
                                position: 'relative',
                                overflow: 'hidden',
                                width: '100%',
                              }}
                              onMouseEnter={(e) => {
                                (e.currentTarget as HTMLElement).style.borderColor =
                                  getSectorColor(sector);
                                (e.currentTarget as HTMLElement).style.transform =
                                  'translateY(-2px)';
                                (e.currentTarget as HTMLElement).style.boxShadow =
                                  `0 4px 12px ${getSectorColor(sector)}20`;
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLElement).style.borderColor =
                                  'var(--border-color)';
                                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                                (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                              }}
                            >
                              <img
                                src={`/images/societes/${stock.symbole}.png`}
                                alt={stock.symbole}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '6px',
                                  objectFit: 'contain',
                                  background: 'white',
                                  padding: '2px',
                                  border: '1px solid rgba(0, 0, 0, 0.05)',
                                  flexShrink: 0,
                                }}
                              />
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: '14px',
                                    fontWeight: '700',
                                    color: 'var(--text-main)',
                                    marginBottom: '2px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                  }}
                                >
                                  {stock.symbole}
                                  {user?.role?.name === 'demo_user' &&
                                    !ALLOWED_DEMO_SYMBOLS.includes(stock.symbole.toUpperCase()) && (
                                      <span
                                        style={{ fontSize: '12px' }}
                                        title='Verrouillé en mode démo'
                                      >
                                        🔒
                                      </span>
                                    )}
                                </span>
                                <span
                                  style={{
                                    fontSize: '10px',
                                    color: 'var(--text-muted)',
                                    lineHeight: '1.2',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 1,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {stock.nom_societe}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Pulse animation */}
              <style>{`
                @keyframes pulse {
                  0%, 100% { opacity: 0.3; transform: scale(1); }
                  50% { opacity: 1; transform: scale(1.5); }
                }
              `}</style>
            </div>
          ) : (
            <>
              {/* Demo banner for restricted stocks */}
              {user?.role?.name === 'demo_user' &&
                selectedStock &&
                !ALLOWED_DEMO_SYMBOLS.includes(selectedStock.toUpperCase()) && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 16px',
                      background:
                        'linear-gradient(135deg, rgba(79, 70, 229, 0.08), rgba(124, 58, 237, 0.08))',
                      border: '1px solid rgba(79, 70, 229, 0.2)',
                      borderRadius: '10px',
                      marginBottom: '16px',
                      fontSize: '13px',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>🔒</span>
                    <span>
                      <strong style={{ color: 'var(--text-main)' }}>Mode Démo</strong> — Aperçu et
                      analyse disponibles pour toutes les sociétés. Les fonctionnalités avancées
                      (Indice Valeur, Nia) sont réservées à <strong>SNTS</strong>,{' '}
                      <strong>ORAC</strong> et <strong>CIEC</strong> en démo.
                    </span>
                    <button
                      onClick={() => navigate('/upgrade')}
                      style={{
                        marginLeft: 'auto',
                        flexShrink: 0,
                        padding: '5px 12px',
                        background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Accès complet
                    </button>
                  </div>
                )}

              {activeTab === 'overview' && stockDetails && (
                <StockOverview
                  stockDetails={stockDetails}
                  quotations={quotations}
                  capitalisation={capitalisation}
                  chartPeriod={chartPeriod}
                  setChartPeriod={setChartPeriod}
                  CHART_PERIOD_OPTIONS={CHART_PERIOD_OPTIONS}
                  latestQuotation={quotations[0]}
                  calculatePerformance={(days) => calculatePerformance(days).value}
                  getWeeklyBreakdown={getWeeklyBreakdown}
                  formatLargeNumber={formatLargeNumber}
                />
              )}

              {activeTab === 'overview' && !stockDetails && (
                <div style={{ textAlign: 'center', padding: '50px', color: '#666' }}>
                  <p>Chargement des données...</p>
                </div>
              )}

              {activeTab === 'analysis' && renderAnalysis()}
              {activeTab === 'dividends' && renderDividends()}
              {activeTab === 'valuation' &&
                (hasPermissionForStock('view_advanced_data') ? (
                  renderValuation()
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '60px 30px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
                    <h3
                      style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        marginBottom: '8px',
                        color: 'var(--text-main)',
                      }}
                    >
                      Fonctionnalité Pro
                    </h3>
                    <p
                      style={{
                        color: 'var(--text-muted)',
                        maxWidth: '420px',
                        lineHeight: '1.6',
                        marginBottom: '24px',
                      }}
                    >
                      L'Indice Valeur est disponible en démo uniquement sur <strong>SNTS</strong>,{' '}
                      <strong>ORAC</strong> et <strong>CIEC</strong>.<br />
                      Passez à un plan payant pour accéder à toutes les sociétés.
                    </p>
                    <button
                      onClick={() => navigate('/upgrade')}
                      style={{
                        padding: '10px 24px',
                        background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(79,70,229,0.3)',
                      }}
                    >
                      Voir les plans
                    </button>
                  </div>
                ))}
              {activeTab === 'peers' &&
                (hasPermissionForStock('view_advanced_data') ? (
                  renderPeers()
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '60px 30px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
                    <h3
                      style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        marginBottom: '8px',
                        color: 'var(--text-main)',
                      }}
                    >
                      Fonctionnalité Pro
                    </h3>
                    <p
                      style={{
                        color: 'var(--text-muted)',
                        maxWidth: '420px',
                        lineHeight: '1.6',
                        marginBottom: '24px',
                      }}
                    >
                      Le comparateur de pairs sectoriels est disponible en démo uniquement sur{' '}
                      <strong>SNTS</strong>, <strong>ORAC</strong> et <strong>CIEC</strong>.<br />
                      Passez à un plan payant pour accéder à toutes les sociétés.
                    </p>
                    <button
                      onClick={() => navigate('/upgrade')}
                      style={{
                        padding: '10px 24px',
                        background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(79,70,229,0.3)',
                      }}
                    >
                      Voir les plans
                    </button>
                  </div>
                ))}
              {activeTab === 'ai_summary' &&
                (hasPermissionForStock('view_advanced_ai_data') ? (
                  renderAISummary()
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '60px 30px',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🤖</div>
                    <h3
                      style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        marginBottom: '8px',
                        color: 'var(--text-main)',
                      }}
                    >
                      Nia — Accès Pro
                    </h3>
                    <p
                      style={{
                        color: 'var(--text-muted)',
                        maxWidth: '420px',
                        lineHeight: '1.6',
                        marginBottom: '24px',
                      }}
                    >
                      L'analyse IA de Nia est disponible en démo uniquement sur{' '}
                      <strong>SNTS</strong>, <strong>ORAC</strong> et <strong>CIEC</strong>.<br />
                      Passez à un plan payant pour accéder à toutes les sociétés.
                    </p>
                    <button
                      onClick={() => navigate('/upgrade')}
                      style={{
                        padding: '10px 24px',
                        background: 'linear-gradient(135deg, #db2777, #9333ea)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        boxShadow: '0 4px 12px rgba(219,39,119,0.3)',
                      }}
                    >
                      Voir les plans
                    </button>
                  </div>
                ))}
            </>
          )}

          {/* Floating Notes Button & Panel */}
          {selectedStock && (
            <>
              <button
                className='ds-notes-btn'
                onClick={() => setIsNotesPanelOpen(true)}
                style={{
                  position: 'fixed',
                  bottom: '24px',
                  right: '24px',
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  color: '#fff',
                  border: 'none',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  cursor: 'pointer',
                  fontSize: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 100,
                }}
                title={`Notes & Tâches pour ${selectedStock}`}
              >
                📝
              </button>
              <StockNotesPanel
                symbole={selectedStock}
                currentPrice={
                  chartData.length > 0
                    ? (chartData[chartData.length - 1] as any)?.close ||
                      (chartData[chartData.length - 1] as any)?.value
                    : undefined
                }
                isOpen={isNotesPanelOpen}
                onClose={() => setIsNotesPanelOpen(false)}
              />
            </>
          )}
        </div>
      )}
    </SiteLayout>
  );
};

export default DataScreen;
